/**
 * Sandbox detail page component.
 *
 * @module pages/SandboxDetailPage
 */

import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router';
import {
  ArrowLeft,
  Trash2,
  Copy,
  Check,
  Camera,
  Terminal,
  Plus,
  ExternalLink,
  X,
  FolderOpen,
  FolderGit2,
  Info,
  Square,
  Play,
  RefreshCw,
  HardDrive,
  MoreHorizontal,
  Link2,
  Clock,
  ScrollText,
  Pencil,
} from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  LoadingPage,
  Badge,
  ConfirmModal,
  Modal,
  Input,
  LabelsEditor,
  getSandboxStateBadgeVariant,
  SlidePanel,
  EgressPolicyEditor,
  sanitizeEgressHeaders,
  IpAccessControlEditor,
  Select,
  TagInput,
  LayoutSwitcher,
  ContainerTabBar,
} from '@/components/ui';
import type { LabelsEditorRef } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import {
  SandboxTerminal,
  SandboxMonitor,
  FileExplorer,
  ProcessesPanel,
  LogStreamPanel,
} from '@/components/terminal';
import {
  useSandbox,
  useDeleteSandbox,
  useCreateSnapshot,
  useCommitSandbox,
  useAddPort,
  useRemovePort,
  usePatchPort,
  useSetEgressPolicy,
  useSetLifecyclePolicy,
  useStopSandbox,
  useResumeSandbox,
  useEgressDecisions,
  usePreviewMode,
  useConnections,
  useSandboxLayout,
  usePortDetectorToast,
  useAddConnection,
  useProjects,
  useAddVolumeMount,
  useAddPodVolumeMounts,
  useVolumes,
  useSecrets,
} from '@/hooks';
import { useSandboxGroupVolumes, useSandboxGroupSecrets } from '@/hooks/useSandboxGroups';
import {
  ConnectionsSection,
  StatefulConnectionsSection,
  EgressEntryRow,
  buildUnifiedEgress,
  type UnifiedEgressEntry,
} from '@/components/connectors';
import { AltLayout } from './layouts';
import type {
  EgressHostRule,
  EgressPolicy,
  EgressPolicyAction,
  EgressPolicyRule,
  SandboxPort,
  EgressDecisionEntry,
  PortProtocol,
  PortActivationMode,
  IpAccessControlAction,
  PortIpAccessControlRule,
  SandboxLifecyclePolicy,
  SandboxSuspendMode,
  AutoDeleteTrigger,
  AddPodVolumeMountsRequest,
  PodVolumeDeclaration,
  ContainerMountSpec,
  TrafficInspection,
  StatefulTcpEgressEntry,
} from '@/types';
import { parseDuration, formatDuration, formatDurationLong } from '@/utils';

/**
 * Maximum local disk space (in MB) shared across all pod volumes (local size + blobfuse cache).
 * Must stay in sync with the server-side validation.
 */
const LOCAL_SPACE_BUDGET_MB = 1024;

/**
 * Masks query string parameters in a URL for display (e.g., tokens).
 * The full URL is still used for href, but display shows "?token=****"
 */
function maskUrlQueryString(url: string): string {
  try {
    const urlObj = new URL(url);
    if (urlObj.search) {
      return `${urlObj.origin}${urlObj.pathname}`;
    }
    return url;
  } catch {
    return url;
  }
}

/**
 * Sandbox detail page
 */
export function SandboxDetailPage() {
  const { id, sandboxGroupId: sandboxGroupIdParam } = useParams<{
    id: string;
    sandboxGroupId?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sandboxGroupId = sandboxGroupIdParam ?? searchParams.get('sandboxGroupId') ?? undefined;
  const volumeBasePath = sandboxGroupId
    ? `/sandbox-groups/${encodeURIComponent(sandboxGroupId)}/volumes`
    : '/volumes';
  const {
    data: sandbox,
    isLoading,
    error,
    refetch: refetchSandbox,
  } = useSandbox(id!, sandboxGroupId);
  const deleteMutation = useDeleteSandbox();
  const snapshotMutation = useCreateSnapshot();
  const commitMutation = useCommitSandbox();
  const addPortMutation = useAddPort();
  const removePortMutation = useRemovePort();
  const patchPortMutation = usePatchPort();
  const setEgressPolicyMutation = useSetEgressPolicy();
  const setLifecyclePolicyMutation = useSetLifecyclePolicy();
  const stopMutation = useStopSandbox();
  const resumeMutation = useResumeSandbox();
  const addConnectionMutation = useAddConnection();
  const addVolumeMutation = useAddVolumeMount();
  const addPodVolumeMountsMutation = useAddPodVolumeMounts();
  const { data: userVolumes } = useVolumes(1, 100, !sandboxGroupId);
  const { data: groupVolumes } = useSandboxGroupVolumes(sandboxGroupId ?? '', !!sandboxGroupId);
  const availableVolumes = sandboxGroupId ? groupVolumes : userVolumes;
  const { data: userSecrets } = useSecrets(!sandboxGroupId);
  const { data: groupSecrets } = useSandboxGroupSecrets(sandboxGroupId ?? '', !!sandboxGroupId);
  const availableSecrets = sandboxGroupId ? groupSecrets : userSecrets;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStopConfirmModal, setShowStopConfirmModal] = useState(false);
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [showCommitModal, setShowCommitModal] = useState(false);
  const [showAddPortModal, setShowAddPortModal] = useState(false);
  const [showAddConnectionModal, setShowAddConnectionModal] = useState(false);
  const [showAddVolumeModal, setShowAddVolumeModal] = useState(false);
  const [addVolumeSelectedName, setAddVolumeSelectedName] = useState<string>('');
  const [addVolumeMountpoint, setAddVolumeMountpoint] = useState<string>('');
  const [addVolumeReadOnly, setAddVolumeReadOnly] = useState<boolean>(false);
  const [addVolumeContainerName, setAddVolumeContainerName] = useState<string>('');
  // Pod-specific volume modal state
  const [showPodVolumeModal, setShowPodVolumeModal] = useState(false);
  const [podVolumeMode, setPodVolumeMode] = useState<'new' | 'existing'>('new');
  const [newVolumeKind, setNewVolumeKind] = useState<'AzureBlob' | 'Local' | 'DataDisk'>(
    'AzureBlob'
  );
  const [newVolumeName, setNewVolumeName] = useState('');
  const [newVolumeSize, setNewVolumeSize] = useState('256Mi');
  const [newVolumeCacheLimit, setNewVolumeCacheLimit] = useState('256Mi');
  const [newVolumeReadOnly, setNewVolumeReadOnly] = useState(false);
  const [existingVolumeName, setExistingVolumeName] = useState('');
  const [podMountRows, setPodMountRows] = useState<
    Array<{ containerName: string; mountPath: string; readOnly: boolean }>
  >([{ containerName: '', mountPath: '', readOnly: false }]);

  const resetPodVolumeModal = useCallback(() => {
    setShowPodVolumeModal(false);
    setPodVolumeMode('new');
    setNewVolumeKind('AzureBlob');
    setNewVolumeName('');
    setNewVolumeSize('256Mi');
    setNewVolumeCacheLimit('256Mi');
    setNewVolumeReadOnly(false);
    setExistingVolumeName('');
    setPodMountRows([{ containerName: '', mountPath: '', readOnly: false }]);
  }, []);

  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
  const [showEgressPolicyModal, setShowEgressPolicyModal] = useState(false);
  const [showLifecyclePolicyModal, setShowLifecyclePolicyModal] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [isWaitingForResume, setIsWaitingForResume] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showFileExplorer, setShowFileExplorer] = useState(false);
  const [showLogStream, setShowLogStream] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [snapshotSuccess, setSnapshotSuccess] = useState(false);
  const [snapshotTimeTakenMs, setSnapshotTimeTakenMs] = useState<number>(0);
  const [snapshotLabels, setSnapshotLabels] = useState<Record<string, string>>({});
  const [commitSuccess, setCommitSuccess] = useState(false);
  const [commitTimeTakenMs, setCommitTimeTakenMs] = useState<number>(0);
  const [commitLabels, setCommitLabels] = useState<Record<string, string>>({});
  const [commitDiskImageId, setCommitDiskImageId] = useState<string | null>(null);
  const [newPortNumber, setNewPortNumber] = useState('');
  const [newPortName, setNewPortName] = useState('');
  const [newPortAuthMode, setNewPortAuthMode] = useState<'entraId' | 'github' | 'none'>('entraId');
  const [newPortProtocol, setNewPortProtocol] = useState<PortProtocol>('Http');
  const [newPortActivationMode, setNewPortActivationMode] = useState<PortActivationMode>('Manual');
  const [newPortAuthEmails, setNewPortAuthEmails] = useState<string[]>([]);
  const [newPortAuthEmailSuffixes, setNewPortAuthEmailSuffixes] = useState<string[]>([]);
  const [newPortAuthUsernames, setNewPortAuthUsernames] = useState<string[]>([]);
  const [newPortAuthUsernameSuffixes, setNewPortAuthUsernameSuffixes] = useState<string[]>([]);
  const [newPortAuthObjectIds, setNewPortAuthObjectIds] = useState<string[]>([]);
  const [newPortAuthTenantIds, setNewPortAuthTenantIds] = useState<string[]>([]);
  const [newPortIpDefaultAction, setNewPortIpDefaultAction] =
    useState<IpAccessControlAction>('Allow');
  const [newPortIpRules, setNewPortIpRules] = useState<PortIpAccessControlRule[]>([]);
  const [showIpAccessControl, setShowIpAccessControl] = useState(false);
  const [newPortCorsAllowOrigins, setNewPortCorsAllowOrigins] = useState<string[]>([]);
  const [newPortCorsAllowMethods, setNewPortCorsAllowMethods] = useState<string[]>([]);
  const [newPortCorsAllowHeaders, setNewPortCorsAllowHeaders] = useState<string[]>([]);
  const [newPortCorsAllowCredentials, setNewPortCorsAllowCredentials] = useState(false);
  const [newPortCorsMaxAge, setNewPortCorsMaxAge] = useState('');
  const [showPortCors, setShowPortCors] = useState(false);
  const [portToRemove, setPortToRemove] = useState<SandboxPort | null>(null);
  const [portToEdit, setPortToEdit] = useState<SandboxPort | null>(null);

  // Ref for snapshot labels editor to get uncommitted draft
  const snapshotLabelsEditorRef = useRef<LabelsEditorRef>(null);
  // Ref for commit labels editor to get uncommitted draft
  const commitLabelsEditorRef = useRef<LabelsEditorRef>(null);

  // Mobile overflow menu
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const [egressDefaultAction, setEgressDefaultAction] = useState<EgressPolicyAction>('Allow');
  const [egressHostRules, setEgressHostRules] = useState<EgressHostRule[]>([]);
  const [egressRules, setEgressRules] = useState<EgressPolicyRule[]>([]);
  const [egressTrafficInspection, setEgressTrafficInspection] =
    useState<TrafficInspection>('Legacy');
  const [isManualEgressRefresh, setIsManualEgressRefresh] = useState(false);
  const [copiedNetworkAudit, setCopiedNetworkAudit] = useState(false);

  // Lifecycle policy editor state
  const [lifecycleAutoSuspendEnabled, setLifecycleAutoSuspendEnabled] = useState(false);
  const [lifecycleAutoSuspendInterval, setLifecycleAutoSuspendInterval] = useState('5m');
  const [lifecycleAutoSuspendMode, setLifecycleAutoSuspendMode] =
    useState<SandboxSuspendMode>('Memory');
  const [lifecycleAutoDeleteEnabled, setLifecycleAutoDeleteEnabled] = useState(false);
  const [lifecycleAutoDeleteInterval, setLifecycleAutoDeleteInterval] = useState('1d');
  const [lifecycleAutoDeleteTrigger, setLifecycleAutoDeleteTrigger] =
    useState<AutoDeleteTrigger>('AfterSuspend');

  const isPreviewMode = usePreviewMode();
  const { user } = useAuth();
  const { getConnectionName, connections } = useConnections(sandboxGroupId);

  // Check if sandbox has personal connectors
  const PERSONAL_CONNECTOR_TYPES = useMemo(() => new Set(['office365-adc', 'm365copilot-adc']), []);
  const hasPersonalConnectors = useMemo(() => {
    if (!sandbox?.connections) return false;
    return sandbox.connections.some((connId) => {
      const conn = connections?.find((c) => c.id === connId);
      return conn ? PERSONAL_CONNECTOR_TYPES.has(conn.type.toLowerCase()) : false;
    });
  }, [sandbox?.connections, connections, PERSONAL_CONNECTOR_TYPES]);

  const activeConnectorNames = useMemo(() => {
    if (!sandbox?.connections || !connections) return [];
    return sandbox.connections
      .map((connId) => {
        const conn = connections.find((c) => c.id === connId);
        return conn?.name || getConnectionName(connId) || connId;
      })
      .filter(Boolean);
  }, [sandbox?.connections, connections, getConnectionName]);
  const [sandboxLayout, setSandboxLayout] = useSandboxLayout();
  const [altActiveTab, setAltActiveTab] = useState<string>('audit');
  const isRunning = sandbox?.state?.toLowerCase() === 'running';
  const isStopped =
    sandbox?.state?.toLowerCase() === 'stopped' || sandbox?.state?.toLowerCase() === 'idle';

  // Pod container state
  const isPod = !!sandbox?.sourcesRef?.pod;
  const podContainers = sandbox?.sourcesRef?.pod?.containers ?? [];
  const containerNames = podContainers.map((c) => c.name);
  const [selectedContainer, setSelectedContainer] = useState<string | undefined>(undefined);
  // Initialize selected container when sandbox loads
  useEffect(() => {
    if (containerNames.length > 0 && !selectedContainer) {
      setSelectedContainer(containerNames[0]);
    }
  }, [containerNames, selectedContainer]);

  /** Check if a pod volume is local (not blob) by name. Used to disable navigation for local volumes. */
  const isLocalVol = useMemo(() => {
    const decls = sandbox?.sourcesRef?.pod?.volumes ?? [];
    return (name: string) => decls.some((d) => d.name === name && d.kind === 'Local');
  }, [sandbox?.sourcesRef?.pod?.volumes]);

  // If sandbox belongs to a project, look up the project for token expiry info
  const sandboxProjectId = sandbox?.labels?.projectId;
  const { data: projects } = useProjects();
  const linkedProject = useMemo(
    () => (sandboxProjectId ? projects?.find((p) => p.id === sandboxProjectId) : undefined),
    [sandboxProjectId, projects]
  );

  // Background port detection for ai-coding sandboxes
  usePortDetectorToast({
    sandboxId: id!,
    sandbox,
    isRunning,
    addPortMutation,
  });

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Track previous running state to detect transitions
  const prevIsRunningRef = useRef<boolean | null>(null);

  // Auto-open terminal when sandbox is running on initial load, close when it stops
  useEffect(() => {
    // Skip if sandbox data hasn't loaded yet
    if (sandbox === undefined) {
      return;
    }

    const prevIsRunning = prevIsRunningRef.current;
    prevIsRunningRef.current = isRunning;

    // On initial load when running, open terminal
    if (prevIsRunning === null && isRunning) {
      setShowTerminal(true);
      return;
    }

    // Transition from running to not running - close terminal
    if (prevIsRunning === true && !isRunning) {
      setShowTerminal(false);
    }
  }, [sandbox, isRunning]);

  // Poll sandbox state while waiting for resume, and open terminal when running
  useEffect(() => {
    if (!isWaitingForResume) {
      return;
    }

    // If sandbox is now running, try opening terminal
    if (isRunning) {
      console.log('[SandboxDetailPage] Sandbox is running, opening terminal');
      setShowTerminal(true);
      setIsWaitingForResume(false);
      return;
    }

    // Keep polling while waiting for resume
    const pollInterval = setInterval(() => {
      console.log('[SandboxDetailPage] Polling sandbox state for resume...');
      refetchSandbox();
    }, 1000);

    return () => clearInterval(pollInterval);
  }, [isWaitingForResume, isRunning, refetchSandbox]);

  // Handle terminal connection error (e.g., sandbox stopped or not ready yet)
  // If waiting for resume, retry; otherwise refetch and close
  const handleTerminalConnectionError = useCallback(() => {
    console.log('[SandboxDetailPage] Terminal connection error');
    if (isWaitingForResume) {
      // Still waiting for resume - terminal will retry when re-mounted
      console.log('[SandboxDetailPage] Still waiting for resume, will retry...');
      setShowTerminal(false);
      // Schedule retry after a short delay
      setTimeout(() => {
        if (isRunning) {
          setShowTerminal(true);
        }
      }, 2000);
    } else {
      // Not resuming - sandbox must have stopped
      console.log('[SandboxDetailPage] Sandbox stopped, refetching state');
      refetchSandbox();
      setShowTerminal(false);
    }
  }, [isWaitingForResume, isRunning, refetchSandbox]);

  // Handle resume button click
  const handleResume = useCallback(() => {
    setIsWaitingForResume(true);
    resumeMutation.mutate({ id: id!, sandboxGroupId: sandboxGroupId });
  }, [resumeMutation, id, sandboxGroupId]);

  // Fetch egress decisions only when sandbox is running
  const {
    data: egressDecisions,
    isLoading: isEgressDecisionsLoading,
    error: egressDecisionsError,
    refetch: refetchEgressDecisions,
    dataUpdatedAt: egressDecisionsUpdatedAt,
  } = useEgressDecisions(id!, isRunning, sandboxGroupId);

  useEffect(() => {
    if (!showEgressPolicyModal) {
      return;
    }

    // Seed editor state from sandbox's egress policy when opening.
    if (sandbox?.egressPolicy) {
      setEgressDefaultAction(sandbox.egressPolicy.defaultAction);
      setEgressHostRules(sandbox.egressPolicy.hostRules ?? []);
      setEgressRules(sandbox.egressPolicy.rules ?? []);
      setEgressTrafficInspection(sandbox.egressPolicy.trafficInspection ?? 'Legacy');
    } else {
      setEgressDefaultAction('Allow');
      setEgressHostRules([]);
      setEgressRules([]);
      setEgressTrafficInspection('Legacy');
    }
  }, [showEgressPolicyModal, sandbox]);

  // Seed lifecycle editor state from sandbox data when modal opens
  useEffect(() => {
    if (!showLifecyclePolicyModal) {
      return;
    }

    const autoSuspend = sandbox?.lifecycle?.autoSuspendPolicy;
    setLifecycleAutoSuspendEnabled(autoSuspend?.enabled ?? false);
    setLifecycleAutoSuspendInterval(formatDuration(autoSuspend?.interval ?? 300));
    setLifecycleAutoSuspendMode(autoSuspend?.mode ?? 'Memory');

    const autoDelete = sandbox?.lifecycle?.autoDeletePolicy;
    setLifecycleAutoDeleteEnabled(autoDelete?.enabled ?? false);
    setLifecycleAutoDeleteInterval(formatDuration(autoDelete?.deleteIntervalInSeconds ?? 86400));
    setLifecycleAutoDeleteTrigger(autoDelete?.trigger ?? 'AfterSuspend');
  }, [showLifecyclePolicyModal, sandbox]);

  const handleDelete = () => {
    if (id) {
      deleteMutation.mutate(
        { id, sandboxGroupId: sandboxGroupId },
        {
          onSuccess: () => navigate('/sandboxes'),
        }
      );
    }
  };

  const handleSnapshot = () => {
    if (id) {
      // Include any uncommitted draft label from the labels editor
      const finalLabels = { ...snapshotLabels };
      const draft = snapshotLabelsEditorRef.current?.getDraft();
      if (draft) {
        finalLabels[draft.key] = draft.value;
      }

      const startTime = performance.now();
      snapshotMutation.mutate(
        { id, sandboxGroupId: sandboxGroupId, labels: finalLabels },
        {
          onSuccess: () => {
            const endTime = performance.now();
            setSnapshotTimeTakenMs(Math.round(endTime - startTime));
            setSnapshotSuccess(true);
            setShowSnapshotModal(false);
          },
        }
      );
    }
  };

  const handleCommit = () => {
    if (id) {
      // Include any uncommitted draft label from the labels editor
      const finalLabels = { ...commitLabels };
      const draft = commitLabelsEditorRef.current?.getDraft();
      if (draft) {
        finalLabels[draft.key] = draft.value;
      }

      const startTime = performance.now();
      commitMutation.mutate(
        { id, sandboxGroupId: sandboxGroupId, labels: finalLabels },
        {
          onSuccess: (data) => {
            const endTime = performance.now();
            setCommitTimeTakenMs(Math.round(endTime - startTime));
            setCommitDiskImageId(data.diskImage.id);
            setCommitSuccess(true);
            setShowCommitModal(false);
            setCommitLabels({});
          },
        }
      );
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleManualEgressRefresh = () => {
    setIsManualEgressRefresh(true);
    refetchEgressDecisions().finally(() => {
      setIsManualEgressRefresh(false);
    });
  };

  const handleCopyNetworkAudit = () => {
    const unified = buildUnifiedEgress(egressDecisions);

    const lines: string[] = [];

    // Header with timestamp
    if (egressDecisionsUpdatedAt > 0) {
      lines.push(
        `Network Audit - Last updated: ${new Date(egressDecisionsUpdatedAt).toLocaleString()}`
      );
      lines.push('');
    }

    const formatEntry = (entry: UnifiedEgressEntry): string => {
      const ts = new Date(entry.timestampMs).toLocaleString();
      if (entry.kind === 'http') {
        const connInfo = entry.connectionId
          ? ` [Connector: ${entry.connectionName ?? entry.connectionId}]`
          : '';
        return `[HTTP] ${entry.method} ${entry.url} - ${ts}${connInfo}`;
      }
      const parts = [`[${entry.protocolLabel}] ${entry.endpoint}`];
      if (entry.database) parts.push(`db=${entry.database}`);
      if (entry.proxyLoginName) parts.push(`login=${entry.proxyLoginName}`);
      if (entry.metricsAreFinal) {
        parts.push(`out=${entry.bytesOut ?? 0}B in=${entry.bytesIn ?? 0}B`);
      }
      parts.push(entry.connState);
      return `${parts.join(' ')} - ${ts}`;
    };

    // Denied egress (HTTP requests + TCP connections)
    lines.push(`=== Denied (${unified.denied.length}) ===`);
    if (unified.denied.length > 0) {
      unified.denied.forEach((entry) => lines.push(formatEntry(entry)));
    } else {
      lines.push('No denied egress.');
    }
    lines.push('');

    // Allowed egress (HTTP requests + TCP connections)
    lines.push(`=== Allowed (${unified.allowed.length}) ===`);
    if (unified.allowed.length > 0) {
      unified.allowed.forEach((entry) => lines.push(formatEntry(entry)));
    } else {
      lines.push('No allowed egress recorded.');
    }

    navigator.clipboard.writeText(lines.join('\n'));
    setCopiedNetworkAudit(true);
    setTimeout(() => setCopiedNetworkAudit(false), 2000);
  };

  const handleAddPort = () => {
    const portNumber = parseInt(newPortNumber, 10);
    if (!portNumber || isNaN(portNumber) || portNumber < 1 || portNumber > 65535) {
      return;
    }
    if (id) {
      // Build auth config based on user selections
      let authConfig;
      if (hasPersonalConnectors && user?.email) {
        // Personal connectors: force Entra ID auth locked to caller's email
        authConfig = {
          entraId: {
            enabled: true,
            emails: [user.email],
            emailSuffixes: [],
            objectIds: [],
            tenantIds: [],
          },
        };
      } else if (newPortAuthMode === 'none') {
        // Anonymous access (no auth required)
        authConfig = { anonymous: true };
      } else if (newPortAuthMode === 'github') {
        authConfig = {
          github: {
            enabled: true,
            emails: newPortAuthEmails,
            emailSuffixes: newPortAuthEmailSuffixes,
            usernames: newPortAuthUsernames,
            usernameSuffixes: newPortAuthUsernameSuffixes,
          },
        };
      } else {
        authConfig = {
          entraId: {
            enabled: true,
            emails: newPortAuthEmails,
            emailSuffixes: newPortAuthEmailSuffixes,
            objectIds: newPortAuthObjectIds,
            tenantIds: newPortAuthTenantIds,
          },
        };
      }

      const ipAccessControl =
        newPortIpRules.length > 0 || newPortIpDefaultAction !== 'Allow'
          ? { defaultAction: newPortIpDefaultAction, rules: newPortIpRules }
          : undefined;
      const parsedMaxAge = newPortCorsMaxAge.trim()
        ? parseInt(newPortCorsMaxAge.trim(), 10)
        : undefined;
      const cors =
        newPortCorsAllowOrigins.length > 0 ||
        newPortCorsAllowMethods.length > 0 ||
        newPortCorsAllowHeaders.length > 0 ||
        newPortCorsAllowCredentials ||
        parsedMaxAge !== undefined
          ? {
              allowOrigins: newPortCorsAllowOrigins,
              allowMethods: newPortCorsAllowMethods,
              allowHeaders: newPortCorsAllowHeaders,
              allowCredentials: newPortCorsAllowCredentials || undefined,
              maxAge: Number.isFinite(parsedMaxAge) ? parsedMaxAge : undefined,
            }
          : undefined;

      addPortMutation.mutate(
        {
          sandboxId: id,
          sandboxGroupId: sandboxGroupId,
          port: portNumber,
          name: newPortName.trim() || undefined,
          auth: authConfig,
          protocol: newPortProtocol,
          activationMode: newPortActivationMode,
          ipAccessControl,
          cors,
        },
        {
          onSuccess: () => {
            setShowAddPortModal(false);
            setPortToEdit(null);
            setNewPortNumber('');
            setNewPortName('');
            setNewPortAuthMode('entraId');
            setNewPortProtocol('Http');
            setNewPortActivationMode('Manual');
            setNewPortAuthEmails([]);
            setNewPortAuthEmailSuffixes([]);
            setNewPortAuthUsernames([]);
            setNewPortAuthUsernameSuffixes([]);
            setNewPortAuthObjectIds([]);
            setNewPortAuthTenantIds([]);
            setNewPortIpDefaultAction('Allow');
            setNewPortIpRules([]);
            setShowIpAccessControl(false);
            setNewPortCorsAllowOrigins([]);
            setNewPortCorsAllowMethods([]);
            setNewPortCorsAllowHeaders([]);
            setNewPortCorsAllowCredentials(false);
            setNewPortCorsMaxAge('');
            setShowPortCors(false);
          },
        }
      );
    }
  };

  const handleRemovePort = () => {
    if (id && portToRemove) {
      removePortMutation.mutate(
        {
          sandboxId: id,
          sandboxGroupId: sandboxGroupId,
          port: portToRemove.port,
          name: portToRemove.name,
        },
        {
          onSuccess: () => {
            setPortToRemove(null);
          },
        }
      );
    }
  };

  const handleEditPort = (port: SandboxPort) => {
    // Pre-fill the port modal state with the existing port's values
    setPortToEdit(port);
    setNewPortNumber(String(port.port));
    setNewPortName(port.name ?? '');
    setNewPortProtocol(port.protocol ?? 'Http');
    setNewPortActivationMode(port.activationMode ?? 'Manual');

    // Determine auth mode from the port's auth config
    if (port.auth?.anonymous) {
      setNewPortAuthMode('none');
    } else if (port.auth?.github?.enabled) {
      setNewPortAuthMode('github');
      setNewPortAuthEmails(port.auth.github.emails ?? []);
      setNewPortAuthEmailSuffixes(port.auth.github.emailSuffixes ?? []);
      setNewPortAuthUsernames(port.auth.github.usernames ?? []);
      setNewPortAuthUsernameSuffixes(port.auth.github.usernameSuffixes ?? []);
    } else if (port.auth?.entraId?.enabled) {
      setNewPortAuthMode('entraId');
      setNewPortAuthEmails(port.auth.entraId.emails ?? []);
      setNewPortAuthEmailSuffixes(port.auth.entraId.emailSuffixes ?? []);
      setNewPortAuthObjectIds(port.auth.entraId.objectIds ?? []);
      setNewPortAuthTenantIds(port.auth.entraId.tenantIds ?? []);
    } else {
      setNewPortAuthMode('entraId');
    }

    // IP access control
    if (port.ipAccessControl) {
      setNewPortIpDefaultAction(port.ipAccessControl.defaultAction);
      setNewPortIpRules(port.ipAccessControl.rules ?? []);
      setShowIpAccessControl(true);
    }

    // CORS
    if (port.cors) {
      setNewPortCorsAllowOrigins(port.cors.allowOrigins ?? []);
      setNewPortCorsAllowMethods(port.cors.allowMethods ?? []);
      setNewPortCorsAllowHeaders(port.cors.allowHeaders ?? []);
      setNewPortCorsAllowCredentials(port.cors.allowCredentials ?? false);
      setNewPortCorsMaxAge(
        port.cors.maxAge !== null && port.cors.maxAge !== undefined ? String(port.cors.maxAge) : ''
      );
      setShowPortCors(true);
    }

    setShowAddPortModal(true);
  };

  const handlePatchPort = () => {
    if (!id || !portToEdit) return;

    let authConfig;
    if (hasPersonalConnectors && user?.email) {
      authConfig = {
        entraId: {
          enabled: true,
          emails: [user.email],
          emailSuffixes: [],
          objectIds: [],
          tenantIds: [],
        },
      };
    } else if (newPortAuthMode === 'none') {
      authConfig = { anonymous: true };
    } else if (newPortAuthMode === 'github') {
      authConfig = {
        github: {
          enabled: true,
          emails: newPortAuthEmails,
          emailSuffixes: newPortAuthEmailSuffixes,
          usernames: newPortAuthUsernames,
          usernameSuffixes: newPortAuthUsernameSuffixes,
        },
      };
    } else {
      authConfig = {
        entraId: {
          enabled: true,
          emails: newPortAuthEmails,
          emailSuffixes: newPortAuthEmailSuffixes,
          objectIds: newPortAuthObjectIds,
          tenantIds: newPortAuthTenantIds,
        },
      };
    }

    const ipAccessControl =
      newPortIpRules.length > 0 || newPortIpDefaultAction !== 'Allow'
        ? { defaultAction: newPortIpDefaultAction, rules: newPortIpRules }
        : undefined;
    const parsedMaxAge = newPortCorsMaxAge.trim()
      ? parseInt(newPortCorsMaxAge.trim(), 10)
      : undefined;
    const cors =
      newPortCorsAllowOrigins.length > 0 ||
      newPortCorsAllowMethods.length > 0 ||
      newPortCorsAllowHeaders.length > 0 ||
      newPortCorsAllowCredentials ||
      parsedMaxAge !== undefined
        ? {
            allowOrigins: newPortCorsAllowOrigins,
            allowMethods: newPortCorsAllowMethods,
            allowHeaders: newPortCorsAllowHeaders,
            allowCredentials: newPortCorsAllowCredentials || undefined,
            maxAge: Number.isFinite(parsedMaxAge) ? parsedMaxAge : undefined,
          }
        : undefined;

    patchPortMutation.mutate(
      {
        sandboxId: id,
        sandboxGroupId: sandboxGroupId,
        port: portToEdit.port,
        name: portToEdit.name,
        auth: authConfig,
        protocol: newPortProtocol,
        activationMode: newPortActivationMode,
        ipAccessControl,
        cors,
      },
      {
        onSuccess: () => {
          setShowAddPortModal(false);
          setPortToEdit(null);
          setNewPortNumber('');
          setNewPortName('');
          setNewPortAuthMode('entraId');
          setNewPortProtocol('Http');
          setNewPortActivationMode('Manual');
          setNewPortAuthEmails([]);
          setNewPortAuthEmailSuffixes([]);
          setNewPortAuthUsernames([]);
          setNewPortAuthUsernameSuffixes([]);
          setNewPortAuthObjectIds([]);
          setNewPortAuthTenantIds([]);
          setNewPortIpDefaultAction('Allow');
          setNewPortIpRules([]);
          setShowIpAccessControl(false);
          setNewPortCorsAllowOrigins([]);
          setNewPortCorsAllowMethods([]);
          setNewPortCorsAllowHeaders([]);
          setNewPortCorsAllowCredentials(false);
          setNewPortCorsMaxAge('');
          setShowPortCors(false);
        },
      }
    );
  };

  const handleAddConnection = () => {
    if (!id || !selectedConnectionId) {
      return;
    }
    addConnectionMutation.mutate(
      {
        sandboxId: id,
        sandboxGroupId: sandboxGroupId,
        connectionId: selectedConnectionId,
      },
      {
        onSuccess: () => {
          setShowAddConnectionModal(false);
          setSelectedConnectionId('');
        },
      }
    );
  };

  const handleSaveEgressPolicy = () => {
    if (!id) {
      return;
    }

    const trimmedRules: EgressHostRule[] = egressHostRules.map((r) => ({
      pattern: r.pattern.trim(),
      action: r.action,
    }));

    if (trimmedRules.some((r) => !r.pattern)) {
      return;
    }

    const policyToSave: EgressPolicy = {
      defaultAction: egressDefaultAction,
      hostRules: trimmedRules.map((r) => ({
        pattern: r.pattern,
        ...(r.action ? { action: r.action } : {}),
      })),
      rules:
        egressRules.length > 0
          ? egressRules
              .filter((r) => r.match.host.trim())
              .map((r) => ({
                ...r,
                action: {
                  ...r.action,
                  headers: sanitizeEgressHeaders(r.action.headers),
                },
              }))
          : undefined,
      trafficInspection: egressTrafficInspection !== 'Full' ? egressTrafficInspection : undefined,
    };

    setEgressPolicyMutation.mutate(
      { sandboxId: id, sandboxGroupId: sandboxGroupId, policy: policyToSave },
      {
        onSuccess: () => {
          setShowEgressPolicyModal(false);
        },
      }
    );
  };

  const handleSaveLifecyclePolicy = () => {
    if (!id) {
      return;
    }

    const interval = parseDuration(lifecycleAutoSuspendInterval);
    if (lifecycleAutoSuspendEnabled && (interval === null || interval <= 0)) {
      return;
    }

    const deleteInterval = parseDuration(lifecycleAutoDeleteInterval);
    if (lifecycleAutoDeleteEnabled && (deleteInterval === null || deleteInterval <= 0)) {
      return;
    }

    const policy: SandboxLifecyclePolicy = {
      autoSuspendPolicy: {
        enabled: lifecycleAutoSuspendEnabled,
        ...(lifecycleAutoSuspendEnabled && interval !== null
          ? { interval, mode: lifecycleAutoSuspendMode }
          : {}),
      },
      autoDeletePolicy: {
        enabled: lifecycleAutoDeleteEnabled,
        ...(lifecycleAutoDeleteEnabled && deleteInterval !== null
          ? { deleteIntervalInSeconds: deleteInterval, trigger: lifecycleAutoDeleteTrigger }
          : {}),
      },
    };

    setLifecyclePolicyMutation.mutate(
      { sandboxId: id, sandboxGroupId: sandboxGroupId, policy },
      {
        onSuccess: () => {
          setShowLifecyclePolicyModal(false);
        },
      }
    );
  };

  const effectiveEgressPolicy = sandbox?.egressPolicy;
  const egressPolicyDisplayDefault = effectiveEgressPolicy?.defaultAction ?? 'Allow';
  const egressPolicyDisplayRules = effectiveEgressPolicy?.hostRules ?? [];
  const egressPolicyDisplayAdvancedRules = effectiveEgressPolicy?.rules ?? [];

  // Redirect to group-scoped URL if sandbox belongs to a group but URL doesn't reflect it
  useEffect(() => {
    if (sandbox?.sandboxGroupId && !sandboxGroupId) {
      navigate(
        `/sandbox-groups/${encodeURIComponent(sandbox.sandboxGroupId)}/sandboxes/${encodeURIComponent(id!)}`,
        { replace: true }
      );
    }
  }, [sandbox?.sandboxGroupId, sandboxGroupId, id, navigate]);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error || !sandbox) {
    return (
      <div>
        <PageHeader
          title="Sandbox Not Found"
          breadcrumbs={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Sandboxes', href: '/sandboxes' },
            { label: 'Not Found' },
          ]}
        />
        <Card>
          <CardBody>
            <p className="text-gray-500">The requested sandbox could not be found.</p>
            <Link to="/sandboxes">
              <Button
                variant="secondary"
                className="mt-4"
                leftIcon={<ArrowLeft className="h-4 w-4" />}
              >
                Back to Sandboxes
              </Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        compact
        title={
          <span className="flex items-center gap-3">
            {sandbox.id}
            {sandbox.state && (
              <Badge variant={getSandboxStateBadgeVariant(sandbox.state)}>{sandbox.state}</Badge>
            )}
            {isPod && <Badge variant="info">Pod: {podContainers.length} containers</Badge>}
          </span>
        }
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Sandboxes', href: '/sandboxes' },
        ]}
        action={
          <>
            {/* Layout switcher + Back button + Actions overflow menu */}
            <div className="flex items-center gap-2">
              {showTerminal && isRunning && (
                <LayoutSwitcher layout={sandboxLayout} onChange={setSandboxLayout} />
              )}
              <Link to="/sandboxes">
                <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />}>
                  Back
                </Button>
              </Link>
              <div className="relative" ref={mobileMenuRef}>
                <Button
                  variant="secondary"
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  leftIcon={<MoreHorizontal className="h-4 w-4" />}
                >
                  Actions
                </Button>
                {showMobileMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-lg z-50">
                    <button
                      onClick={() => {
                        setShowInfoPanel(true);
                        setShowMobileMenu(false);
                      }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"
                    >
                      <Info className="h-4 w-4" />
                      Details
                    </button>
                    <button
                      onClick={() => {
                        setShowTerminal(!showTerminal);
                        setShowMobileMenu(false);
                      }}
                      disabled={!isRunning}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Terminal className="h-4 w-4" />
                      {showTerminal ? 'Hide Terminal' : 'Open Terminal'}
                    </button>
                    <button
                      onClick={() => {
                        setShowFileExplorer(!showFileExplorer);
                        setShowMobileMenu(false);
                      }}
                      disabled={!isRunning}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FolderOpen className="h-4 w-4" />
                      {showFileExplorer ? 'Hide Files' : 'Browse Files'}
                    </button>
                    <button
                      onClick={() => {
                        setShowLogStream(!showLogStream);
                        setShowMobileMenu(false);
                      }}
                      disabled={!isRunning}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ScrollText className="h-4 w-4" />
                      {showLogStream ? 'Hide Logs' : 'Stream Logs'}
                    </button>
                    <button
                      onClick={() => {
                        setShowSnapshotModal(true);
                        setShowMobileMenu(false);
                      }}
                      disabled={!isRunning}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Camera className="h-4 w-4" />
                      Snapshot
                    </button>
                    <button
                      onClick={() => {
                        setShowCommitModal(true);
                        setShowMobileMenu(false);
                      }}
                      disabled={!isRunning}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <HardDrive className="h-4 w-4" />
                      Commit Image
                    </button>
                    {isStopped ? (
                      <button
                        onClick={() => {
                          handleResume();
                          setShowMobileMenu(false);
                        }}
                        disabled={resumeMutation.isPending || isWaitingForResume}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Play className="h-4 w-4" />
                        {resumeMutation.isPending || isWaitingForResume ? 'Resuming...' : 'Resume'}
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (sandbox?.lifecycle?.autoSuspendPolicy?.mode === 'None') {
                            setShowStopConfirmModal(true);
                          } else {
                            stopMutation.mutate({ id: id!, sandboxGroupId: sandboxGroupId });
                          }
                          setShowMobileMenu(false);
                        }}
                        disabled={stopMutation.isPending || !isRunning}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Square className="h-4 w-4" />
                        {stopMutation.isPending ? 'Stopping...' : 'Stop'}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowDeleteModal(true);
                        setShowMobileMenu(false);
                      }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] border-t border-gray-200 dark:border-[#2a2a2a]"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        }
      />

      {/* Project + Token Expiry banner (visible on main page) */}
      {linkedProject && (
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-[#2a2a2a] bg-[#161616] px-4 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <FolderGit2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <Link
              to="/projects"
              className="text-sm font-medium text-blue-400 hover:underline truncate"
            >
              {linkedProject.name}
            </Link>
            <a
              href={linkedProject.repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-300 flex-shrink-0"
              title={linkedProject.repoFullName}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            {(() => {
              const s = linkedProject.tokenStatus;
              const dot =
                s === 'expired'
                  ? 'bg-red-500'
                  : s === 'expiring-soon'
                    ? 'bg-yellow-400'
                    : 'bg-green-500';
              const cls =
                s === 'expired'
                  ? 'text-red-400'
                  : s === 'expiring-soon'
                    ? 'text-yellow-400'
                    : 'text-green-400';
              const lbl =
                s === 'expired'
                  ? 'Token expired'
                  : s === 'expiring-soon'
                    ? 'Token expiring soon'
                    : 'Token valid';
              const exp = linkedProject.tokenExpiresAt;
              const expText = exp
                ? ` · ${s === 'expired' ? 'Expired' : 'Expires'} ${new Date(exp).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}`
                : ' · No expiry set';
              return (
                <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cls}`}>
                  <span className={`w-2 h-2 rounded-full ${dot}`} />
                  {lbl}
                  <span className="text-gray-500 font-normal">{expText}</span>
                </span>
              );
            })()}
          </div>
        </div>
      )}

      {/* ==================== CLASSIC LAYOUT ==================== */}
      {sandboxLayout === 'classic' && (
        <>
          {/* Terminal and Network Audit */}
          {showTerminal &&
            isRunning &&
            (isPod ? (
              <>
                {/* Pod: one terminal per container, side by side */}
                <div
                  className={`mb-4 grid gap-4 lg:gap-6 grid-cols-1 ${podContainers.length === 2 ? 'lg:grid-cols-2' : podContainers.length >= 3 ? 'lg:grid-cols-3' : ''}`}
                >
                  {podContainers.map((c) => (
                    <Card
                      key={c.name}
                      className="h-[350px] sm:h-[400px] lg:h-[500px] flex flex-col overflow-hidden"
                    >
                      <div className="px-3 py-1.5 border-b border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#111] flex-shrink-0">
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                          {c.name}
                        </span>
                      </div>
                      <div className="flex-1 min-h-0">
                        <SandboxTerminal
                          key={`term-pod-${c.name}`}
                          sandboxId={id!}
                          sandboxGroupId={sandboxGroupId}
                          containerName={c.name}
                          onClose={() => setShowTerminal(false)}
                          onConnectionError={handleTerminalConnectionError}
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              /* Single-container: unchanged layout */
              <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
                <Card className="h-[350px] sm:h-[400px] lg:h-[500px] lg:col-span-2 flex flex-col overflow-hidden">
                  <div className="flex-1 min-h-0">
                    <SandboxTerminal
                      sandboxId={id!}
                      sandboxGroupId={sandboxGroupId}
                      onClose={() => setShowTerminal(false)}
                      onConnectionError={handleTerminalConnectionError}
                    />
                  </div>
                </Card>
                <Card className="h-[300px] sm:h-[350px] lg:h-[500px] lg:col-span-1 flex flex-col">
                  <NetworkAuditContent
                    isLoading={isEgressDecisionsLoading}
                    error={egressDecisionsError}
                    egressDecisions={egressDecisions}
                    egressDecisionsUpdatedAt={egressDecisionsUpdatedAt}
                    copiedNetworkAudit={copiedNetworkAudit}
                    isManualEgressRefresh={isManualEgressRefresh}
                    onCopy={handleCopyNetworkAudit}
                    onRefresh={handleManualEgressRefresh}
                    getConnectionName={getConnectionName}
                  />
                </Card>
              </div>
            ))}

          {/* Terminal only (when not running) */}
          {showTerminal &&
            !isRunning &&
            (isPod ? (
              <div
                className={`mb-6 grid gap-4 lg:gap-6 grid-cols-1 ${podContainers.length === 2 ? 'lg:grid-cols-2' : podContainers.length >= 3 ? 'lg:grid-cols-3' : ''}`}
              >
                {podContainers.map((c) => (
                  <Card key={c.name} className="h-[500px] flex flex-col overflow-hidden">
                    <div className="px-3 py-1.5 border-b border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#111] flex-shrink-0">
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        {c.name}
                      </span>
                    </div>
                    <div className="flex-1 min-h-0">
                      <SandboxTerminal
                        key={`term-pod-stopped-${c.name}`}
                        sandboxId={id!}
                        sandboxGroupId={sandboxGroupId}
                        containerName={c.name}
                        onClose={() => setShowTerminal(false)}
                        onConnectionError={handleTerminalConnectionError}
                      />
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="mb-6">
                <Card className="h-[500px] flex flex-col overflow-hidden">
                  <div className="flex-1 min-h-0">
                    <SandboxTerminal
                      sandboxId={id!}
                      sandboxGroupId={sandboxGroupId}
                      onClose={() => setShowTerminal(false)}
                      onConnectionError={handleTerminalConnectionError}
                    />
                  </div>
                </Card>
              </div>
            ))}

          {/* Stats, Volumes, Network Audit — pod: combined grid; single: separate sections */}
          {isRunning && isPod && (
            <div className="mb-6 relative">
              <div className="lg:pr-[calc(33.333%+0.75rem)]">
                <div className="flex flex-col gap-4">
                  <SandboxMonitor sandboxId={id!} sandboxGroupId={sandboxGroupId} />
                  {/* Compact Volumes inline */}
                  <Card>
                    <CardHeader
                      title="Volumes"
                      icon={<HardDrive className="h-5 w-5 text-primary-500" />}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!isRunning}
                          onClick={() => setShowPodVolumeModal(true)}
                          title={
                            isRunning
                              ? 'Add volume to sandbox'
                              : 'Sandbox must be running to add volumes'
                          }
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Volume
                        </Button>
                      }
                    />
                    <CardBody>
                      {(() => {
                        const topLevelVolumes = (sandbox?.volumes ?? []).map((vol) => ({
                          volumeName: vol.volumeName,
                          mountPath: vol.mountpoint,
                          readOnly: !!vol.readOnly,
                          containerName: undefined as string | undefined,
                          isLocal: isLocalVol(vol.volumeName),
                        }));
                        const podVolumes2 = (sandbox?.sourcesRef?.pod?.containers ?? []).flatMap(
                          (c) =>
                            (c.volumeMounts ?? []).map((v) => ({
                              volumeName: v.name,
                              mountPath: v.mountPath,
                              readOnly: !!v.readOnly,
                              containerName: c.name,
                              isLocal: isLocalVol(v.name),
                            }))
                        );
                        const allVolumes = [...topLevelVolumes, ...podVolumes2];
                        const filteredVolumes = selectedContainer
                          ? allVolumes.filter(
                              (v) => v.containerName === selectedContainer || !v.containerName
                            )
                          : allVolumes;

                        return allVolumes.length > 0 ? (
                          <div>
                            <ContainerTabBar
                              containers={containerNames}
                              selected={selectedContainer ?? ''}
                              onSelect={setSelectedContainer}
                              className="mb-3"
                            />
                            <div className="overflow-x-auto max-h-[200px] overflow-y-auto">
                              <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-white dark:bg-[#0a0a0a]">
                                  <tr className="border-b border-gray-200 dark:border-gray-700">
                                    <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                      Volume
                                    </th>
                                    <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                      Mount Path
                                    </th>
                                    <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                                      Access
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredVolumes.map((vol, vIdx) => (
                                    <tr
                                      key={`${vol.containerName ?? 'top'}-${vol.volumeName}-${vIdx}`}
                                      className={`border-b border-gray-100 dark:border-gray-800 transition-colors ${vol.isLocal ? '' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer'}`}
                                      onClick={
                                        vol.isLocal
                                          ? undefined
                                          : () =>
                                              navigate(
                                                `${volumeBasePath}/${encodeURIComponent(vol.volumeName)}`
                                              )
                                      }
                                    >
                                      <td
                                        className={`px-3 py-1.5 font-mono text-xs ${vol.isLocal ? 'text-gray-700 dark:text-gray-300' : 'text-primary-600 dark:text-primary-400'}`}
                                      >
                                        {vol.volumeName}
                                      </td>
                                      <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300 font-mono text-xs">
                                        {vol.mountPath}
                                      </td>
                                      <td className="px-3 py-1.5">
                                        <Badge variant={vol.readOnly ? 'warning' : 'success'}>
                                          {vol.readOnly ? 'RO' : 'RW'}
                                        </Badge>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                            No volumes mounted
                          </p>
                        );
                      })()}
                    </CardBody>
                  </Card>
                </div>
              </div>
              <div
                className="hidden lg:block absolute top-0 right-0 bottom-0"
                style={{ width: 'calc(33.333% - 0.75rem)' }}
              >
                <Card className="h-full flex flex-col overflow-hidden">
                  <NetworkAuditContent
                    isLoading={isEgressDecisionsLoading}
                    error={egressDecisionsError}
                    egressDecisions={egressDecisions}
                    egressDecisionsUpdatedAt={egressDecisionsUpdatedAt}
                    copiedNetworkAudit={copiedNetworkAudit}
                    isManualEgressRefresh={isManualEgressRefresh}
                    onCopy={handleCopyNetworkAudit}
                    onRefresh={handleManualEgressRefresh}
                    getConnectionName={getConnectionName}
                  />
                </Card>
              </div>
            </div>
          )}
          {isRunning && !isPod && (
            <div className="mb-6">
              <SandboxMonitor sandboxId={id!} sandboxGroupId={sandboxGroupId} />
            </div>
          )}

          {/* Volumes Section — non-pod only (pod volumes are inline above) */}
          {!isPod && (
            <div className="mb-6">
              <Card>
                <CardHeader
                  title="Volumes"
                  icon={<HardDrive className="h-5 w-5 text-primary-500" />}
                  action={
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!isRunning}
                      onClick={() => setShowAddVolumeModal(true)}
                      title={
                        isRunning
                          ? 'Add volume to sandbox'
                          : 'Sandbox must be running to add volumes'
                      }
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Volume
                    </Button>
                  }
                />
                <CardBody>
                  {(() => {
                    const allVolumes = (sandbox?.volumes ?? []).map((vol) => ({
                      volumeName: vol.volumeName,
                      mountPath: vol.mountpoint,
                      readOnly: !!vol.readOnly,
                    }));

                    return allVolumes.length > 0 ? (
                      <div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-200 dark:border-gray-700">
                                <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                                  Volume Name
                                </th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                                  Mount Path
                                </th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                                  Access
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {allVolumes.map((vol, vIdx) => (
                                <tr
                                  key={`${vol.volumeName}-${vIdx}`}
                                  className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                                  onClick={() =>
                                    navigate(
                                      `${volumeBasePath}/${encodeURIComponent(vol.volumeName)}`
                                    )
                                  }
                                >
                                  <td className="px-3 py-2 text-primary-600 dark:text-primary-400 font-mono">
                                    {vol.volumeName}
                                  </td>
                                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 font-mono">
                                    {vol.mountPath}
                                  </td>
                                  <td className="px-3 py-2">
                                    <Badge variant={vol.readOnly ? 'warning' : 'success'}>
                                      {vol.readOnly ? 'Read Only' : 'Read/Write'}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <HardDrive className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No volumes mounted</p>
                        <p className="text-xs mt-1">
                          Volumes can be mounted during sandbox creation
                        </p>
                      </div>
                    );
                  })()}
                </CardBody>
              </Card>
            </div>
          )}

          {/* Log Stream Panel */}
          {showLogStream && isRunning && (
            <div className="mb-6">
              <LogStreamPanel
                sandboxId={id!}
                sandboxGroupId={sandboxGroupId}
                onClose={() => setShowLogStream(false)}
              />
            </div>
          )}

          {/* Connections Section - Preview Only */}
          {isPreviewMode && isRunning && (
            <div className="mb-6 space-y-6">
              <ConnectionsSection
                allowedEntries={egressDecisions?.networkEgress?.allowed ?? []}
                deniedEntries={egressDecisions?.networkEgress?.denied ?? []}
                isLoading={isEgressDecisionsLoading}
              />
              <StatefulConnectionsSection
                entries={egressDecisions?.statefulTcp?.connections ?? []}
                isLoading={isEgressDecisionsLoading}
              />
            </div>
          )}

          {/* File Explorer */}
          {showFileExplorer && isRunning && (
            <div className="mb-6">
              <Card className="h-[350px] sm:h-[400px] lg:h-[500px] flex flex-col overflow-hidden">
                <ContainerTabBar
                  containers={containerNames}
                  selected={selectedContainer ?? ''}
                  onSelect={setSelectedContainer}
                />
                <div className="flex-1 min-h-0">
                  <FileExplorer
                    key={`files-classic-${selectedContainer}`}
                    sandboxId={id!}
                    sandboxGroupId={sandboxGroupId}
                    containerName={selectedContainer}
                  />
                </div>
              </Card>
            </div>
          )}
        </>
      )}

      {/* ==================== ALTERNATIVE LAYOUT ==================== */}
      {sandboxLayout === 'alt' && showTerminal && isRunning && (
        <AltLayout
          onTabChange={setAltActiveTab}
          terminal={
            isPod ? (
              <div className="h-full flex gap-0.5">
                {podContainers.map((c) => (
                  <div key={c.name} className="flex-1 min-w-0 flex flex-col h-full">
                    <div className="px-3 py-1 border-b border-[#2a2a2a] bg-[#111] flex-shrink-0">
                      <span className="text-[10px] font-semibold text-gray-400">{c.name}</span>
                    </div>
                    <div className="flex-1 min-h-0">
                      <SandboxTerminal
                        key={`term-alt-pod-${c.name}`}
                        sandboxId={id!}
                        sandboxGroupId={sandboxGroupId}
                        containerName={c.name}
                        onClose={() => setShowTerminal(false)}
                        onConnectionError={handleTerminalConnectionError}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <SandboxTerminal
                key={`term-alt-single`}
                sandboxId={id!}
                sandboxGroupId={sandboxGroupId}
                onClose={() => setShowTerminal(false)}
                onConnectionError={handleTerminalConnectionError}
              />
            )
          }
          networkAudit={
            <div className="h-full flex flex-col">
              <NetworkAuditInnerContent
                isLoading={isEgressDecisionsLoading}
                error={egressDecisionsError}
                egressDecisions={egressDecisions}
                egressDecisionsUpdatedAt={egressDecisionsUpdatedAt}
                copiedNetworkAudit={copiedNetworkAudit}
                isManualEgressRefresh={isManualEgressRefresh}
                onCopy={handleCopyNetworkAudit}
                onRefresh={handleManualEgressRefresh}
                getConnectionName={getConnectionName}
              />
            </div>
          }
          monitor={
            <div className="h-full overflow-auto p-4">
              <SandboxMonitor sandboxId={id!} sandboxGroupId={sandboxGroupId} compact />
            </div>
          }
          processes={
            <div className="h-full flex flex-col">
              <ContainerTabBar
                containers={containerNames}
                selected={selectedContainer ?? ''}
                onSelect={setSelectedContainer}
              />
              <div className="flex-1 min-h-0">
                <ProcessesPanel
                  key={`proc-alt-${selectedContainer}`}
                  sandboxId={id!}
                  sandboxGroupId={sandboxGroupId}
                  containerName={selectedContainer}
                  compact
                  isActive={altActiveTab === 'processes'}
                />
              </div>
            </div>
          }
          fileExplorer={
            <div className="h-full flex flex-col">
              <ContainerTabBar
                containers={containerNames}
                selected={selectedContainer ?? ''}
                onSelect={setSelectedContainer}
              />
              <div className="flex-1 min-h-0">
                <FileExplorer
                  key={`files-alt-${selectedContainer}`}
                  sandboxId={id!}
                  sandboxGroupId={sandboxGroupId}
                  containerName={selectedContainer}
                  compact
                />
              </div>
            </div>
          }
          connections={
            isPreviewMode ? (
              <div className="h-full overflow-auto p-4 space-y-6">
                <ConnectionsSection
                  inline
                  allowedEntries={egressDecisions?.networkEgress?.allowed ?? []}
                  deniedEntries={egressDecisions?.networkEgress?.denied ?? []}
                  isLoading={isEgressDecisionsLoading}
                />
                <StatefulConnectionsSection
                  inline
                  entries={egressDecisions?.statefulTcp?.connections ?? []}
                  isLoading={isEgressDecisionsLoading}
                />
              </div>
            ) : undefined
          }
          volumes={(() => {
            const topLevelVols = (sandbox?.volumes ?? []).map((vol) => ({
              name: vol.volumeName,
              mountPath: vol.mountpoint,
              readOnly: !!vol.readOnly,
              containerName: undefined as string | undefined,
              isLocal: isLocalVol(vol.volumeName),
            }));
            const podVols = (sandbox?.sourcesRef?.pod?.containers ?? []).flatMap((c) =>
              (c.volumeMounts ?? []).map((v) => ({
                name: v.name,
                mountPath: v.mountPath,
                readOnly: !!v.readOnly,
                containerName: c.name,
                isLocal: isLocalVol(v.name),
              }))
            );
            const allVols = [...topLevelVols, ...podVols];
            if (!isPod && allVols.length === 0) return undefined;
            const filteredVols =
              isPod && selectedContainer
                ? allVols.filter((v) => v.containerName === selectedContainer || !v.containerName)
                : allVols;
            return (
              <div className="h-full flex flex-col min-h-0 overflow-hidden">
                {isPod && (
                  <ContainerTabBar
                    containers={containerNames}
                    selected={selectedContainer ?? ''}
                    onSelect={setSelectedContainer}
                  />
                )}
                {/* Toolbar */}
                <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-200 dark:border-[#2a2a2a] flex-shrink-0">
                  <HardDrive className="w-3 h-3 text-gray-400" />
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">
                    {filteredVols.length} volume{filteredVols.length !== 1 ? 's' : ''} mounted
                  </span>
                  {isPod && (
                    <button
                      type="button"
                      onClick={() => setShowPodVolumeModal(true)}
                      className="ml-auto inline-flex items-center gap-1 text-[10px] text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                    >
                      <Plus className="h-3 w-3" />
                      Add Volume
                    </button>
                  )}
                </div>

                {/* Table or empty state */}
                {filteredVols.length > 0 ? (
                  <div className="flex-1 min-h-0 overflow-auto">
                    <table className="w-full text-[11px] font-mono">
                      <thead className="sticky top-0 z-10">
                        <tr className="bg-gray-50 dark:bg-[#111] border-b border-gray-200 dark:border-[#2a2a2a]">
                          <th className="px-2 py-1 text-left font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            Name
                          </th>
                          <th className="px-2 py-1 text-left font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            Mount Path
                          </th>
                          <th className="px-2 py-1 text-left font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            Access
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredVols.map((vol, vIdx) => (
                          <tr
                            key={`${vol.containerName ?? 'top'}-${vol.name}-${vIdx}`}
                            className={`border-b border-gray-100 dark:border-[#2a2a2a]/50 transition-colors ${vol.isLocal ? '' : 'hover:bg-gray-50 dark:hover:bg-[#161616] cursor-pointer'}`}
                            onClick={
                              vol.isLocal
                                ? undefined
                                : () =>
                                    navigate(`${volumeBasePath}/${encodeURIComponent(vol.name)}`)
                            }
                          >
                            <td
                              className={`px-2 py-1 whitespace-nowrap ${vol.isLocal ? 'text-gray-600 dark:text-gray-300' : 'text-primary-600 dark:text-primary-400'}`}
                            >
                              {vol.name}
                            </td>
                            <td className="px-2 py-1 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                              {vol.mountPath}
                            </td>
                            <td className="px-2 py-1 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                              {vol.readOnly ? 'ro' : 'rw'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500">
                    No volumes mounted
                  </div>
                )}
              </div>
            );
          })()}
        />
      )}

      {/* Alternative fallback: not running or terminal hidden */}
      {sandboxLayout === 'alt' && (!showTerminal || !isRunning) && (
        <>
          {showTerminal &&
            !isRunning &&
            (isPod ? (
              <div
                className={`mb-6 grid gap-4 lg:gap-6 grid-cols-1 ${podContainers.length === 2 ? 'lg:grid-cols-2' : podContainers.length >= 3 ? 'lg:grid-cols-3' : ''}`}
              >
                {podContainers.map((c) => (
                  <Card key={c.name} className="h-[500px] flex flex-col overflow-hidden">
                    <div className="px-3 py-1.5 border-b border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#111] flex-shrink-0">
                      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                        {c.name}
                      </span>
                    </div>
                    <div className="flex-1 min-h-0">
                      <SandboxTerminal
                        key={`term-alt-fallback-pod-${c.name}`}
                        sandboxId={id!}
                        sandboxGroupId={sandboxGroupId}
                        containerName={c.name}
                        onClose={() => setShowTerminal(false)}
                        onConnectionError={handleTerminalConnectionError}
                      />
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="mb-6">
                <Card className="h-[500px] flex flex-col overflow-hidden">
                  <div className="flex-1 min-h-0">
                    <SandboxTerminal
                      sandboxId={id!}
                      sandboxGroupId={sandboxGroupId}
                      onClose={() => setShowTerminal(false)}
                      onConnectionError={handleTerminalConnectionError}
                    />
                  </div>
                </Card>
              </div>
            ))}
          {isRunning && (
            <div className="mb-6">
              <SandboxMonitor sandboxId={id!} sandboxGroupId={sandboxGroupId} compact />
            </div>
          )}
          {isPreviewMode && isRunning && (
            <div className="mb-6 space-y-6">
              <ConnectionsSection
                allowedEntries={egressDecisions?.networkEgress?.allowed ?? []}
                deniedEntries={egressDecisions?.networkEgress?.denied ?? []}
                isLoading={isEgressDecisionsLoading}
              />
              <StatefulConnectionsSection
                entries={egressDecisions?.statefulTcp?.connections ?? []}
                isLoading={isEgressDecisionsLoading}
              />
            </div>
          )}
          {showFileExplorer && isRunning && (
            <div className="mb-6">
              <Card className="h-[350px] sm:h-[400px] lg:h-[500px] flex flex-col overflow-hidden">
                <ContainerTabBar
                  containers={containerNames}
                  selected={selectedContainer ?? ''}
                  onSelect={setSelectedContainer}
                />
                <div className="flex-1 min-h-0">
                  <FileExplorer
                    key={`files-alt-fallback-${selectedContainer}`}
                    sandboxId={id!}
                    sandboxGroupId={sandboxGroupId}
                    containerName={selectedContainer}
                  />
                </div>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Snapshot Success Modal */}
      <Modal
        isOpen={snapshotSuccess}
        onClose={() => setSnapshotSuccess(false)}
        title="Snapshot Created Successfully"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSnapshotSuccess(false)}>
              Close
            </Button>
            <Link
              to={
                sandboxGroupId
                  ? `/sandbox-groups/${encodeURIComponent(sandboxGroupId)}/snapshots`
                  : '/snapshots'
              }
            >
              <Button onClick={() => setSnapshotSuccess(false)}>View Snapshots</Button>
            </Link>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-full">
              <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                A snapshot of your sandbox has been created.
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Time taken: {snapshotTimeTakenMs.toLocaleString()} ms
          </p>
        </div>
      </Modal>

      {/* Commit Disk Image Success Modal */}
      <Modal
        isOpen={commitSuccess}
        onClose={() => setCommitSuccess(false)}
        title="Disk Image Created Successfully"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCommitSuccess(false)}>
              Close
            </Button>
            <Link to="/disk-images">
              <Button onClick={() => setCommitSuccess(false)}>View Disk Images</Button>
            </Link>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-full">
              <HardDrive className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                A new disk image has been created from your sandbox.
              </p>
            </div>
          </div>
          {commitDiskImageId && (
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Disk Image ID</p>
              <p className="font-mono text-sm text-gray-900 dark:text-gray-100 break-all">
                {commitDiskImageId}
              </p>
            </div>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Time taken: {commitTimeTakenMs.toLocaleString()} ms
          </p>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Sandbox"
        message={`Are you sure you want to delete sandbox "${sandbox.id}"? This action cannot be undone and any data will be lost.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Stop Confirmation Modal (for suspend mode = None) */}
      <ConfirmModal
        isOpen={showStopConfirmModal}
        onClose={() => setShowStopConfirmModal(false)}
        onConfirm={() => {
          stopMutation.mutate({ id: id!, sandboxGroupId: sandboxGroupId });
          setShowStopConfirmModal(false);
        }}
        title="Stop Sandbox"
        message={`This sandbox has suspend mode set to "None". Stopping it will discard all in-VM state (memory, disk changes). When resumed, it will start fresh from the original base image. Are you sure you want to proceed?`}
        confirmText="Stop"
        variant="primary"
        isLoading={stopMutation.isPending}
      />

      {/* Snapshot Modal */}
      <Modal
        isOpen={showSnapshotModal}
        onClose={() => setShowSnapshotModal(false)}
        title="Create Snapshot"
        description="Create a snapshot of this sandbox."
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowSnapshotModal(false)}
              disabled={snapshotMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSnapshot} isLoading={snapshotMutation.isPending} disabled={!id}>
              Create
            </Button>
          </>
        }
      >
        <LabelsEditor
          ref={snapshotLabelsEditorRef}
          value={snapshotLabels}
          onChange={setSnapshotLabels}
          helperText="Optional key/value labels to attach to the snapshot."
          requireValue={false}
        />
      </Modal>

      {/* Commit Disk Image Modal */}
      <Modal
        isOpen={showCommitModal}
        onClose={() => {
          setShowCommitModal(false);
          setCommitLabels({});
        }}
        title="Commit Disk Image"
        description="Create a new disk image with the changes made in this running sandbox."
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowCommitModal(false);
                setCommitLabels({});
              }}
              disabled={commitMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleCommit} isLoading={commitMutation.isPending} disabled={!id}>
              Commit
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Note:</strong> The running sandbox will be briefly paused during the commit
              process. The new disk image can be used to create new sandboxes with your current
              changes.
            </p>
          </div>
          <LabelsEditor
            ref={commitLabelsEditorRef}
            value={commitLabels}
            onChange={setCommitLabels}
            helperText="Optional key/value labels to attach to the new disk image."
            requireValue={false}
          />
        </div>
      </Modal>

      {/* Add/Edit Port Modal */}
      <Modal
        isOpen={showAddPortModal}
        onClose={() => {
          setShowAddPortModal(false);
          setPortToEdit(null);
          setNewPortNumber('');
          setNewPortName('');
          setNewPortAuthMode('entraId');
          setNewPortProtocol('Http');
          setNewPortActivationMode('Manual');
          setNewPortAuthEmails([]);
          setNewPortAuthEmailSuffixes([]);
          setNewPortAuthUsernames([]);
          setNewPortAuthUsernameSuffixes([]);
          setNewPortAuthObjectIds([]);
          setNewPortAuthTenantIds([]);
          setNewPortIpDefaultAction('Allow');
          setNewPortIpRules([]);
          setShowIpAccessControl(false);
          setNewPortCorsAllowOrigins([]);
          setNewPortCorsAllowMethods([]);
          setNewPortCorsAllowHeaders([]);
          setNewPortCorsAllowCredentials(false);
          setNewPortCorsMaxAge('');
          setShowPortCors(false);
        }}
        title={portToEdit ? 'Edit Port' : 'Expose Port'}
        description={
          portToEdit
            ? 'Update the configuration for this port.'
            : 'Expose a port from your sandbox to get an HTTPS URL for external access.'
        }
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddPortModal(false);
                setPortToEdit(null);
                setNewPortNumber('');
                setNewPortName('');
                setNewPortAuthMode('entraId');
                setNewPortProtocol('Http');
                setNewPortActivationMode('Manual');
                setNewPortAuthEmails([]);
                setNewPortAuthEmailSuffixes([]);
                setNewPortAuthUsernames([]);
                setNewPortAuthUsernameSuffixes([]);
                setNewPortAuthObjectIds([]);
                setNewPortAuthTenantIds([]);
                setNewPortIpDefaultAction('Allow');
                setNewPortIpRules([]);
                setShowIpAccessControl(false);
                setNewPortCorsAllowOrigins([]);
                setNewPortCorsAllowMethods([]);
                setNewPortCorsAllowHeaders([]);
                setNewPortCorsAllowCredentials(false);
                setNewPortCorsMaxAge('');
                setShowPortCors(false);
              }}
              disabled={addPortMutation.isPending || patchPortMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={portToEdit ? handlePatchPort : handleAddPort}
              isLoading={portToEdit ? patchPortMutation.isPending : addPortMutation.isPending}
              disabled={
                !newPortNumber ||
                parseInt(newPortNumber, 10) < 1 ||
                parseInt(newPortNumber, 10) > 65535
              }
            >
              {portToEdit ? 'Save Changes' : 'Add Port'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                label="Port"
                type="number"
                placeholder="8080"
                value={newPortNumber}
                onChange={(e) => setNewPortNumber(e.target.value)}
                min={1}
                max={65535}
                required
                disabled={!!portToEdit}
              />
            </div>
            {!hasPersonalConnectors && (
              <div className="flex-1">
                <Input
                  label="Name"
                  placeholder="Optional"
                  value={newPortName}
                  onChange={(e) => setNewPortName(e.target.value)}
                />
              </div>
            )}
          </div>

          {hasPersonalConnectors && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <svg
                className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Port access is restricted to <strong>{user?.email}</strong> because this sandbox
                uses personal connectors.
              </p>
            </div>
          )}

          {!hasPersonalConnectors && (
            <>
              <Select
                label="Authentication"
                value={newPortAuthMode}
                onChange={(e) => {
                  const mode = e.target.value as 'entraId' | 'github' | 'none';
                  setNewPortAuthMode(mode);
                  // Clear provider-specific fields when switching
                  setNewPortAuthUsernames([]);
                  setNewPortAuthUsernameSuffixes([]);
                  setNewPortAuthObjectIds([]);
                  setNewPortAuthTenantIds([]);
                }}
                options={[
                  { value: 'entraId', label: 'Microsoft Entra ID' },
                  { value: 'github', label: 'GitHub' },
                  { value: 'none', label: 'No authentication (public)' },
                ]}
              />
              {newPortAuthMode !== 'none' && (
                <div className="space-y-3">
                  <TagInput
                    label="Allowed Emails"
                    value={newPortAuthEmails}
                    onChange={setNewPortAuthEmails}
                    placeholder="user@example.com"
                    helperText="Leave empty to allow any authenticated user"
                    validate={(value) => {
                      const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
                      if (!emailPattern.test(value)) {
                        return 'Invalid email';
                      }
                      return null;
                    }}
                  />
                  <TagInput
                    label="Email Suffixes"
                    value={newPortAuthEmailSuffixes}
                    onChange={setNewPortAuthEmailSuffixes}
                    placeholder="@company.com"
                    validate={(value) => {
                      const suffixPattern = /^@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
                      if (!suffixPattern.test(value)) {
                        return 'Must start with @';
                      }
                      return null;
                    }}
                  />
                  {newPortAuthMode === 'github' && (
                    <>
                      <TagInput
                        label="Usernames"
                        value={newPortAuthUsernames}
                        onChange={setNewPortAuthUsernames}
                        placeholder="octocat"
                        validate={(value) => {
                          const usernamePattern = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,38}$/;
                          if (!usernamePattern.test(value)) {
                            return 'Invalid username';
                          }
                          return null;
                        }}
                      />
                      <TagInput
                        label="Username Suffixes"
                        value={newPortAuthUsernameSuffixes}
                        onChange={setNewPortAuthUsernameSuffixes}
                        placeholder="_microsoft"
                        helperText="Allow GitHub usernames ending with this suffix"
                        validate={(value) => {
                          const suffixPattern = /^_[a-zA-Z0-9_-]+$/;
                          if (!suffixPattern.test(value)) {
                            return 'Must start with _';
                          }
                          return null;
                        }}
                      />
                    </>
                  )}
                  {newPortAuthMode === 'entraId' && (
                    <>
                      <TagInput
                        label="Object IDs"
                        value={newPortAuthObjectIds}
                        onChange={setNewPortAuthObjectIds}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        helperText="User or group object IDs"
                        validate={(value) => {
                          const guidPattern =
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
                          if (!guidPattern.test(value)) {
                            return 'Invalid GUID';
                          }
                          return null;
                        }}
                      />
                      <TagInput
                        label="Tenant IDs"
                        value={newPortAuthTenantIds}
                        onChange={setNewPortAuthTenantIds}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        validate={(value) => {
                          const guidPattern =
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
                          if (!guidPattern.test(value)) {
                            return 'Invalid GUID';
                          }
                          return null;
                        }}
                      />
                    </>
                  )}
                </div>
              )}
              {newPortAuthMode === 'none' && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <svg
                    className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    This port will be accessible to anyone without authentication.
                  </p>
                </div>
              )}
            </>
          )}

          {/* IP Access Control (collapsible) */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              type="button"
              className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg"
              onClick={() => setShowIpAccessControl(!showIpAccessControl)}
            >
              <span>IP Access Control (Optional)</span>
              <span className="text-xs text-gray-400">{showIpAccessControl ? '▲' : '▼'}</span>
            </button>
            {showIpAccessControl && (
              <div className="px-3 pb-3">
                <IpAccessControlEditor
                  defaultAction={newPortIpDefaultAction}
                  onDefaultActionChange={setNewPortIpDefaultAction}
                  rules={newPortIpRules}
                  onRulesChange={setNewPortIpRules}
                />
              </div>
            )}
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <button
              type="button"
              className="w-full flex items-center justify-between p-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg"
              onClick={() => setShowPortCors(!showPortCors)}
            >
              <span>CORS (Optional)</span>
              <span className="text-xs text-gray-400">{showPortCors ? '▲' : '▼'}</span>
            </button>
            {showPortCors && (
              <div className="px-3 pb-3 space-y-3">
                <TagInput
                  label="Allow Origins"
                  value={newPortCorsAllowOrigins}
                  onChange={setNewPortCorsAllowOrigins}
                  placeholder="https://example.com"
                />
                <TagInput
                  label="Allow Methods"
                  value={newPortCorsAllowMethods}
                  onChange={setNewPortCorsAllowMethods}
                  placeholder="GET"
                />
                <TagInput
                  label="Allow Headers"
                  value={newPortCorsAllowHeaders}
                  onChange={setNewPortCorsAllowHeaders}
                  placeholder="Content-Type"
                />
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    checked={newPortCorsAllowCredentials}
                    onChange={(e) => setNewPortCorsAllowCredentials(e.target.checked)}
                  />
                  Allow credentials
                </label>
                <Input
                  label="Max age (seconds)"
                  type="number"
                  min={0}
                  placeholder="86400"
                  value={newPortCorsMaxAge}
                  onChange={(e) => setNewPortCorsMaxAge(e.target.value)}
                />
              </div>
            )}
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
              Advanced options
            </summary>
            <div className="mt-3 space-y-3">
              <Select
                label="Protocol"
                value={newPortProtocol}
                onChange={(e) => setNewPortProtocol(e.target.value as PortProtocol)}
                options={[
                  { value: 'Http', label: 'HTTP/1.1' },
                  { value: 'Http2', label: 'HTTP/2 (gRPC)' },
                ]}
              />
              <div>
                <Select
                  label="Activation mode"
                  value={newPortActivationMode}
                  onChange={(e) => setNewPortActivationMode(e.target.value as PortActivationMode)}
                  options={[
                    { value: 'Manual', label: 'Manual' },
                    { value: 'OnDemand', label: 'On demand' },
                  ]}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  On demand auto-resumes a suspended sandbox when the port is accessed. Manual
                  serves the port only while the sandbox is running.
                </p>
              </div>
            </div>
          </details>
        </div>
      </Modal>

      {/* Add Connection Modal */}
      <Modal
        isOpen={showAddConnectionModal}
        onClose={() => {
          setShowAddConnectionModal(false);
          setSelectedConnectionId('');
        }}
        title="Add Connection"
        description="Select a connection to add to this running sandbox."
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddConnectionModal(false);
                setSelectedConnectionId('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddConnection}
              disabled={!selectedConnectionId || addConnectionMutation.isPending}
            >
              {addConnectionMutation.isPending ? 'Adding...' : 'Add Connection'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label="Connection"
            value={selectedConnectionId}
            onChange={(e) => setSelectedConnectionId(e.target.value)}
            options={[
              { value: '', label: 'Select a connection...' },
              ...(connections || []).map((conn) => ({
                value: conn.id,
                label: conn.name,
              })),
            ]}
            required
          />
          {addConnectionMutation.isError && (
            <div className="text-sm text-danger-600 dark:text-danger-400">
              Failed to add connection. Please try again.
            </div>
          )}
        </div>
      </Modal>

      {/* Egress Policy Modal */}
      <Modal
        isOpen={showEgressPolicyModal}
        onClose={() => setShowEgressPolicyModal(false)}
        title="Network Egress Policy"
        description="Control outbound network access for this sandbox. Changes apply only while the sandbox is running."
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowEgressPolicyModal(false)}
              disabled={setEgressPolicyMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEgressPolicy}
              isLoading={setEgressPolicyMutation.isPending}
              disabled={!isRunning || egressHostRules.some((r) => !r.pattern || !r.pattern.trim())}
            >
              Save
            </Button>
          </>
        }
      >
        <EgressPolicyEditor
          defaultAction={egressDefaultAction}
          onDefaultActionChange={setEgressDefaultAction}
          hostRules={egressHostRules}
          onHostRulesChange={setEgressHostRules}
          rules={egressRules}
          onRulesChange={setEgressRules}
          connectorNames={activeConnectorNames}
          trafficInspection={egressTrafficInspection}
          onTrafficInspectionChange={setEgressTrafficInspection}
          availableSecrets={availableSecrets}
          sandboxGroupId={sandboxGroupId}
        />
      </Modal>

      {/* Lifecycle Policy Modal */}
      <Modal
        isOpen={showLifecyclePolicyModal}
        onClose={() => setShowLifecyclePolicyModal(false)}
        title="Lifecycle Policy"
        description="Configure auto-suspend and auto-delete behavior. Changes apply to running and stopped sandboxes."
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowLifecyclePolicyModal(false)}
              disabled={setLifecyclePolicyMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveLifecyclePolicy}
              isLoading={setLifecyclePolicyMutation.isPending}
              disabled={
                (lifecycleAutoSuspendEnabled &&
                  (parseDuration(lifecycleAutoSuspendInterval) === null ||
                    (parseDuration(lifecycleAutoSuspendInterval) ?? 0) <= 0)) ||
                (lifecycleAutoDeleteEnabled &&
                  (parseDuration(lifecycleAutoDeleteInterval) === null ||
                    (parseDuration(lifecycleAutoDeleteInterval) ?? 0) <= 0))
              }
            >
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          {/* Auto-Suspend */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={lifecycleAutoSuspendEnabled}
                onChange={(e) => setLifecycleAutoSuspendEnabled(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Enable Auto-Suspend
              </span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
              Automatically suspend the sandbox after a period of inactivity.
            </p>
            {lifecycleAutoSuspendEnabled && (
              <div className="ml-6 space-y-3">
                <Input
                  label="Idle Timeout"
                  value={lifecycleAutoSuspendInterval}
                  onChange={(e) => setLifecycleAutoSuspendInterval(e.target.value)}
                  placeholder="5m"
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Use a duration like 1s, 5m, 2h, or 1d.{' '}
                  {parseDuration(lifecycleAutoSuspendInterval) !== null && (
                    <span className="font-medium">
                      = {formatDurationLong(parseDuration(lifecycleAutoSuspendInterval)!)}
                    </span>
                  )}
                </p>
                {parseDuration(lifecycleAutoSuspendInterval) === null &&
                  lifecycleAutoSuspendInterval.trim() !== '' && (
                    <p className="text-xs text-danger-600 dark:text-danger-400">
                      Invalid duration format. Use values like 1s, 5m, 2h, 1d.
                    </p>
                  )}
                {parseDuration(lifecycleAutoSuspendInterval) !== null &&
                  parseDuration(lifecycleAutoSuspendInterval)! <= 0 && (
                    <p className="text-xs text-danger-600 dark:text-danger-400">
                      Interval must be greater than 0.
                    </p>
                  )}
                <Select
                  label="Suspend Mode"
                  value={lifecycleAutoSuspendMode}
                  onChange={(e) =>
                    setLifecycleAutoSuspendMode(e.target.value as SandboxSuspendMode)
                  }
                  options={[
                    { value: 'Memory', label: 'Memory (full VM snapshot)' },
                    { value: 'Disk', label: 'Disk (preserves disk only, VM restarts fresh)' },
                    { value: 'None', label: 'None (no snapshot — VM state discarded on stop)' },
                  ]}
                />
              </div>
            )}
          </div>

          {/* Auto-Delete */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={lifecycleAutoDeleteEnabled}
                onChange={(e) => setLifecycleAutoDeleteEnabled(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Enable Auto-Delete
              </span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
              Automatically delete the sandbox after a configured duration.
            </p>
            {lifecycleAutoDeleteEnabled && (
              <div className="ml-6 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Delete trigger
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="lifecycleAutoDeleteTrigger"
                        value="AfterSuspend"
                        checked={lifecycleAutoDeleteTrigger === 'AfterSuspend'}
                        onChange={(e) =>
                          setLifecycleAutoDeleteTrigger(e.target.value as AutoDeleteTrigger)
                        }
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        After suspend
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="lifecycleAutoDeleteTrigger"
                        value="AfterCreation"
                        checked={lifecycleAutoDeleteTrigger === 'AfterCreation'}
                        onChange={(e) =>
                          setLifecycleAutoDeleteTrigger(e.target.value as AutoDeleteTrigger)
                        }
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        After creation
                      </span>
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {lifecycleAutoDeleteTrigger === 'AfterCreation'
                      ? 'Timer starts when the sandbox is created, regardless of state.'
                      : 'Timer starts after the sandbox is suspended.'}
                  </p>
                  {lifecycleAutoDeleteTrigger === 'AfterCreation' && (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                      ⚠️ Auto-suspend may not take effect if the sandbox is deleted first.
                    </p>
                  )}
                </div>
                <Input
                  label="Delete after"
                  value={lifecycleAutoDeleteInterval}
                  onChange={(e) => setLifecycleAutoDeleteInterval(e.target.value)}
                  placeholder="1d"
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Use a duration like 30m, 2h, 1d, or 7d.{' '}
                  {parseDuration(lifecycleAutoDeleteInterval) !== null && (
                    <span className="font-medium">
                      = {formatDurationLong(parseDuration(lifecycleAutoDeleteInterval)!)}
                    </span>
                  )}
                </p>
                {parseDuration(lifecycleAutoDeleteInterval) === null &&
                  lifecycleAutoDeleteInterval.trim() !== '' && (
                    <p className="text-xs text-danger-600 dark:text-danger-400">
                      Invalid duration format. Use values like 30m, 2h, 1d, 7d.
                    </p>
                  )}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Add Volume Modal */}
      <Modal
        isOpen={showAddVolumeModal}
        onClose={() => {
          setShowAddVolumeModal(false);
          setAddVolumeSelectedName('');
          setAddVolumeMountpoint('');
          setAddVolumeReadOnly(false);
          setAddVolumeContainerName('');
        }}
        title="Add Volume"
        description="Mount a volume to this running sandbox."
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddVolumeModal(false);
                setAddVolumeSelectedName('');
                setAddVolumeMountpoint('');
                setAddVolumeReadOnly(false);
                setAddVolumeContainerName('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (addVolumeSelectedName && addVolumeMountpoint) {
                  addVolumeMutation.mutate(
                    {
                      sandboxId: id!,
                      sandboxGroupId: sandboxGroupId,
                      volumeMount: {
                        volumeName: addVolumeSelectedName,
                        mountpoint: addVolumeMountpoint,
                        readOnly: addVolumeReadOnly,
                      },
                      containerName:
                        isPod && addVolumeContainerName ? addVolumeContainerName : undefined,
                    },
                    {
                      onSuccess: () => {
                        setShowAddVolumeModal(false);
                        setAddVolumeSelectedName('');
                        setAddVolumeMountpoint('');
                        setAddVolumeReadOnly(false);
                        setAddVolumeContainerName('');
                      },
                    }
                  );
                }
              }}
              disabled={
                !addVolumeSelectedName ||
                !addVolumeMountpoint ||
                addVolumeMutation.isPending ||
                (isPod && !addVolumeContainerName)
              }
            >
              {addVolumeMutation.isPending ? 'Adding...' : 'Add Volume'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {isPod && (
            <Select
              label="Container"
              value={addVolumeContainerName}
              onChange={(e) => {
                setAddVolumeContainerName(e.target.value);
                setAddVolumeSelectedName('');
              }}
              options={[
                { value: '', label: 'Select a container...' },
                ...containerNames.map((name) => ({ value: name, label: name })),
              ]}
              required
            />
          )}
          <Select
            label="Volume"
            value={addVolumeSelectedName}
            onChange={(e) => setAddVolumeSelectedName(e.target.value)}
            options={[
              { value: '', label: 'Select a volume...' },
              ...(availableVolumes || [])
                .filter((vol) => {
                  // Exclude volumes already mounted at the top level
                  if (sandbox?.volumes?.some((v) => v.volumeName === vol.volumeName)) return false;
                  // For pods, also exclude volumes already mounted in the selected container
                  if (isPod && addVolumeContainerName) {
                    const container = sandbox?.sourcesRef?.pod?.containers?.find(
                      (c) => c.name === addVolumeContainerName
                    );
                    if (container?.volumeMounts?.some((v) => v.name === vol.volumeName))
                      return false;
                  }
                  return true;
                })
                .map((vol) => ({
                  value: vol.volumeName,
                  label: vol.volumeName,
                })),
            ]}
            required
          />
          <Input
            label="Mount Path"
            value={addVolumeMountpoint}
            onChange={(e) => setAddVolumeMountpoint(e.target.value)}
            placeholder="/mnt/data"
            required
          />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="addVolumeReadOnly"
              checked={addVolumeReadOnly}
              onChange={(e) => setAddVolumeReadOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="addVolumeReadOnly" className="text-sm text-gray-700 dark:text-gray-300">
              Mount as read-only
            </label>
          </div>
          {addVolumeMutation.isError && (
            <div className="text-sm text-danger-600 dark:text-danger-400">
              Failed to add volume. Please try again.
            </div>
          )}
        </div>
      </Modal>

      {/* Pod Add Volume Modal — new/existing volume with multi-container mount rows */}
      <Modal
        isOpen={showPodVolumeModal}
        onClose={resetPodVolumeModal}
        title="Add Pod Volume"
        description="Add a new volume or mount an existing volume into pod containers."
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={resetPodVolumeModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                // Determine volume name based on mode
                const volName = podVolumeMode === 'existing' ? existingVolumeName : newVolumeName;

                // Normalize + validate mount paths
                const validRows = podMountRows
                  .map((r) => ({
                    ...r,
                    mountPath: r.mountPath.trim().replace(/\/+$/, '') || '',
                  }))
                  .filter((r) => r.containerName && r.mountPath.startsWith('/'));
                if (!volName || validRows.length === 0) return;

                // Group rows by container name
                const containerMap = new Map<
                  string,
                  Array<{ name: string; mountPath: string; readOnly?: boolean }>
                >();
                for (const row of validRows) {
                  if (!containerMap.has(row.containerName)) {
                    containerMap.set(row.containerName, []);
                  }
                  containerMap.get(row.containerName)!.push({
                    name: volName,
                    mountPath: row.mountPath,
                    readOnly: row.readOnly || undefined,
                  });
                }
                const containerMounts: ContainerMountSpec[] = Array.from(
                  containerMap.entries()
                ).map(([containerName, volumeMounts]) => ({
                  containerName,
                  volumeMounts,
                }));

                let volumes: PodVolumeDeclaration[] | undefined;
                if (podVolumeMode === 'new') {
                  if (newVolumeKind === 'Local') {
                    volumes = [{ kind: 'Local', name: newVolumeName, size: newVolumeSize }];
                  } else if (newVolumeKind === 'DataDisk') {
                    volumes = [{ kind: 'DataDisk', name: newVolumeName }];
                  } else {
                    volumes = [
                      {
                        kind: 'AzureBlob',
                        name: newVolumeName,
                        fileCacheSizeLimit: newVolumeCacheLimit,
                        readOnly: newVolumeReadOnly || undefined,
                      },
                    ];
                  }
                }

                const request: AddPodVolumeMountsRequest = { volumes, containerMounts };
                addPodVolumeMountsMutation.mutate(
                  {
                    sandboxId: id!,
                    sandboxGroupId,
                    request,
                  },
                  {
                    onSuccess: resetPodVolumeModal,
                  }
                );
              }}
              disabled={
                addPodVolumeMountsMutation.isPending ||
                (podVolumeMode === 'new' &&
                  newVolumeKind === 'AzureBlob' &&
                  (!newVolumeName || !newVolumeCacheLimit)) ||
                (podVolumeMode === 'new' &&
                  newVolumeKind === 'Local' &&
                  (!newVolumeName || !newVolumeSize)) ||
                (podVolumeMode === 'new' && newVolumeKind === 'DataDisk' && !newVolumeName) ||
                (podVolumeMode === 'existing' && !existingVolumeName) ||
                !podMountRows.some((r) => r.containerName && r.mountPath.trim().startsWith('/')) ||
                // Block submit if any duplicate container+mountPath pairs exist
                podMountRows.some(
                  (r, i) =>
                    r.containerName &&
                    r.mountPath.trim() &&
                    podMountRows.some(
                      (other, j) =>
                        j !== i &&
                        other.containerName === r.containerName &&
                        other.mountPath.trim() === r.mountPath.trim()
                    )
                )
              }
            >
              {addPodVolumeMountsMutation.isPending ? 'Adding...' : 'Add Volume'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Mode selector */}
          <div className="flex gap-2">
            <button
              type="button"
              className={clsx(
                'flex-1 py-2 px-3 text-sm font-medium rounded-md border transition-colors',
                podVolumeMode === 'new'
                  ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
              onClick={() => setPodVolumeMode('new')}
            >
              New Volume
            </button>
            <button
              type="button"
              className={clsx(
                'flex-1 py-2 px-3 text-sm font-medium rounded-md border transition-colors',
                podVolumeMode === 'existing'
                  ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              )}
              onClick={() => setPodVolumeMode('existing')}
            >
              Mount Existing Volume
            </button>
          </div>

          {/* New volume fields */}
          {podVolumeMode === 'new' && (
            <div className="space-y-3">
              {/* Space budget indicator */}
              {(() => {
                const parseSize = (s: string): number => {
                  const trimmed = s.trim();
                  if (!trimmed) return 0;
                  // Strict match: "256Mi", "1Gi", "1.5Gi"
                  const strict = trimmed.match(/^(\d+(?:\.\d+)?)\s*(Mi|Gi)$/i);
                  if (strict) {
                    const val = parseFloat(strict[1]);
                    return strict[2].toLowerCase() === 'gi' ? val * 1024 : val;
                  }
                  // Partial match while typing: "256M" → Mi, "1G" → Gi, "512" → Mi
                  const partial = trimmed.match(/^(\d+(?:\.\d+)?)\s*([gG])?/);
                  if (partial) {
                    const val = parseFloat(partial[1]);
                    return partial[2] ? val * 1024 : val;
                  }
                  return 0;
                };
                const existingVolumes = sandbox?.sourcesRef?.pod?.volumes ?? [];
                const usedMB = existingVolumes.reduce((sum, v) => {
                  if (v.kind === 'Local') return sum + parseSize(v.size);
                  if (v.kind === 'AzureBlob') return sum + parseSize(v.fileCacheSizeLimit);
                  // DataDisk volumes do NOT count toward local space budget
                  return sum;
                }, 0);
                const newMB =
                  newVolumeKind === 'Local'
                    ? parseSize(newVolumeSize)
                    : newVolumeKind === 'AzureBlob'
                      ? parseSize(newVolumeCacheLimit)
                      : 0; // DataDisk: no local space impact
                const totalMB = usedMB + newMB;
                const usedPct = Math.min((usedMB / LOCAL_SPACE_BUDGET_MB) * 100, 100);
                const newPct = Math.min((newMB / LOCAL_SPACE_BUDGET_MB) * 100, 100 - usedPct);
                const overBudget = totalMB > LOCAL_SPACE_BUDGET_MB;

                return (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500 dark:text-gray-400">Local space budget</span>
                      <span
                        className={
                          overBudget
                            ? 'text-danger-600 dark:text-danger-400 font-medium'
                            : 'text-gray-600 dark:text-gray-300'
                        }
                      >
                        {usedMB} MB used
                        {newMB > 0 && <> + {newMB} MB new</>}
                        {' / '}
                        {LOCAL_SPACE_BUDGET_MB} MB
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full flex">
                        <div
                          className="bg-gray-400 dark:bg-gray-500 transition-all"
                          style={{ width: `${usedPct}%` }}
                        />
                        <div
                          className={`transition-all ${overBudget ? 'bg-danger-500' : 'bg-primary-500'}`}
                          style={{ width: `${newPct}%` }}
                        />
                      </div>
                    </div>
                    {overBudget && (
                      <p className="text-xs text-danger-600 dark:text-danger-400">
                        Exceeds the 1 GiB limit — server will reject this request.
                      </p>
                    )}
                  </div>
                );
              })()}
              {/* Kind toggle — follows create-page pattern */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Volume Type
                </label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setNewVolumeKind('AzureBlob');
                      setNewVolumeName('');
                    }}
                    className={`px-2.5 py-1 text-xs rounded-md border transition-colors cursor-pointer ${
                      newVolumeKind === 'AzureBlob'
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                        : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    Azure Blob
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNewVolumeKind('Local');
                      setNewVolumeName('');
                    }}
                    className={`px-2.5 py-1 text-xs rounded-md border transition-colors cursor-pointer ${
                      newVolumeKind === 'Local'
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                        : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    Local
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNewVolumeKind('DataDisk');
                      setNewVolumeName('');
                    }}
                    className={`px-2.5 py-1 text-xs rounded-md border transition-colors cursor-pointer ${
                      newVolumeKind === 'DataDisk'
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-400 dark:border-primary-600 text-primary-700 dark:text-primary-300 font-medium'
                        : 'bg-white dark:bg-[#161616] border-gray-300 dark:border-[#333] text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    Data Disk
                  </button>
                </div>
              </div>

              {newVolumeKind === 'AzureBlob' ? (
                <>
                  <div>
                    <Select
                      label="Blob Volume"
                      value={newVolumeName}
                      onChange={(e) => setNewVolumeName(e.target.value)}
                      options={[
                        { value: '', label: 'Select a blob volume...' },
                        ...(availableVolumes ?? [])
                          .filter(
                            (v) =>
                              v.type !== 'DataDisk' &&
                              !(sandbox?.sourcesRef?.pod?.volumes ?? []).some(
                                (pv) => pv.name === v.volumeName
                              )
                          )
                          .map((v) => ({
                            value: v.volumeName,
                            label: v.volumeName,
                          })),
                      ]}
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Pick a registered blob volume. Manage volumes on the Volumes page.
                    </p>
                  </div>
                  <div>
                    <Input
                      label="File Cache Size Limit"
                      value={newVolumeCacheLimit}
                      onChange={(e) => setNewVolumeCacheLimit(e.target.value)}
                      placeholder="256Mi"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Local disk space for the blobfuse2 file cache (e.g. 256Mi, 1Gi).
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="newVolumeReadOnly"
                      checked={newVolumeReadOnly}
                      onChange={(e) => setNewVolumeReadOnly(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label
                      htmlFor="newVolumeReadOnly"
                      className="text-sm text-gray-700 dark:text-gray-300"
                    >
                      Read-only volume
                    </label>
                  </div>
                </>
              ) : newVolumeKind === 'DataDisk' ? (
                <>
                  <div>
                    <Select
                      label="Data Disk Volume"
                      value={newVolumeName}
                      onChange={(e) => setNewVolumeName(e.target.value)}
                      options={[
                        { value: '', label: 'Select a data disk volume...' },
                        ...(availableVolumes ?? [])
                          .filter(
                            (v) =>
                              v.type === 'DataDisk' &&
                              !v.isAttached &&
                              !(sandbox?.sourcesRef?.pod?.volumes ?? []).some(
                                (pv) => pv.name === v.volumeName
                              )
                          )
                          .map((v) => ({
                            value: v.volumeName,
                            label: `${v.volumeName}${v.size ? ` (${v.size})` : ''}`,
                          })),
                      ]}
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Pick a data disk volume. Only available (not attached) data disk volumes are
                      shown.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Input
                      label="Volume Name"
                      value={newVolumeName}
                      onChange={(e) => setNewVolumeName(e.target.value)}
                      placeholder="scratch"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Choose a name for this local volume.
                    </p>
                  </div>
                  <div>
                    <Input
                      label="Local Volume Size"
                      value={newVolumeSize}
                      onChange={(e) => setNewVolumeSize(e.target.value)}
                      placeholder="256Mi"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Disk space allocated for this local volume (e.g. 256Mi, 1Gi).
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Existing volume picker */}
          {podVolumeMode === 'existing' && (
            <div>
              <Select
                label="Pod Volume"
                value={existingVolumeName}
                onChange={(e) => setExistingVolumeName(e.target.value)}
                options={[
                  { value: '', label: 'Select a pod volume...' },
                  ...(sandbox?.sourcesRef?.pod?.volumes ?? []).map((v) => ({
                    value: v.name,
                    label: `${v.name} (${v.kind === 'AzureBlob' ? 'blob' : v.kind === 'DataDisk' ? 'dataDisk' : 'local'})`,
                  })),
                ]}
                required
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Mount a volume that is already declared on this pod into additional containers.
              </p>
            </div>
          )}

          {/* Container mount rows */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Container Mounts
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Specify where to mount the volume inside each container. Mount path must be an
              absolute path (start with /).
            </p>
            <div className="space-y-2">
              {podMountRows.map((row, idx) => {
                const pathTrimmed = row.mountPath.trim();
                const pathInvalid = pathTrimmed.length > 0 && !pathTrimmed.startsWith('/');
                const isDuplicate =
                  row.containerName &&
                  pathTrimmed &&
                  podMountRows.some(
                    (other, otherIdx) =>
                      otherIdx !== idx &&
                      other.containerName === row.containerName &&
                      other.mountPath.trim() === pathTrimmed
                  );
                return (
                  <div key={idx}>
                    <div className="flex items-center gap-2">
                      <select
                        value={row.containerName}
                        onChange={(e) => {
                          const updated = [...podMountRows];
                          updated[idx] = { ...updated[idx], containerName: e.target.value };
                          setPodMountRows(updated);
                        }}
                        className="flex-1 min-w-0 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-2 py-1.5 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">Container...</option>
                        {containerNames.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={row.mountPath}
                        onChange={(e) => {
                          const updated = [...podMountRows];
                          updated[idx] = { ...updated[idx], mountPath: e.target.value };
                          setPodMountRows(updated);
                        }}
                        onBlur={() => {
                          let cleaned = row.mountPath.trim();
                          if (cleaned && !cleaned.startsWith('/')) {
                            cleaned = '/' + cleaned;
                          }
                          while (cleaned.length > 1 && cleaned.endsWith('/')) {
                            cleaned = cleaned.slice(0, -1);
                          }
                          if (cleaned !== row.mountPath) {
                            const updated = [...podMountRows];
                            updated[idx] = { ...updated[idx], mountPath: cleaned };
                            setPodMountRows(updated);
                          }
                        }}
                        placeholder="/mnt/data"
                        className={clsx(
                          'flex-1 min-w-0 rounded-md border text-sm px-2 py-1.5',
                          pathInvalid
                            ? 'border-danger-400 dark:border-danger-500 bg-danger-50 dark:bg-danger-900/20 text-danger-700 dark:text-danger-300'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                        )}
                      />
                      <div className="flex items-center gap-1 shrink-0">
                        <input
                          type="checkbox"
                          checked={row.readOnly}
                          onChange={(e) => {
                            const updated = [...podMountRows];
                            updated[idx] = { ...updated[idx], readOnly: e.target.checked };
                            setPodMountRows(updated);
                          }}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          title="Read-only"
                        />
                        <span className="text-xs text-gray-500 dark:text-gray-400">RO</span>
                      </div>
                      {podMountRows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setPodMountRows(podMountRows.filter((_, i) => i !== idx))}
                          className="text-gray-400 hover:text-danger-500 transition-colors shrink-0"
                          title="Remove row"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {pathInvalid && (
                      <p className="mt-0.5 text-xs text-danger-600 dark:text-danger-400">
                        Mount path must start with /
                      </p>
                    )}
                    {!pathInvalid && isDuplicate && (
                      <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">
                        Duplicate mount path for this container
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() =>
                setPodMountRows([
                  ...podMountRows,
                  { containerName: '', mountPath: '', readOnly: false },
                ])
              }
              className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline"
            >
              + Add another container mount
            </button>
          </div>

          {addPodVolumeMountsMutation.isError && (
            <div className="text-sm text-danger-600 dark:text-danger-400">
              Failed to add pod volumes. Please try again.
            </div>
          )}
        </div>
      </Modal>

      {/* Remove Port Confirmation Modal */}
      <ConfirmModal
        isOpen={!!portToRemove}
        onClose={() => setPortToRemove(null)}
        onConfirm={handleRemovePort}
        title="Remove Port"
        message={`Are you sure you want to remove port ${portToRemove?.name ? `"${portToRemove.name}" (${portToRemove.port})` : portToRemove?.port}? The external URL will no longer be accessible.`}
        confirmText="Remove"
        variant="danger"
        isLoading={removePortMutation.isPending}
      />

      {/* Info Slide Panel */}
      <SlidePanel
        isOpen={showInfoPanel}
        onClose={() => setShowInfoPanel(false)}
        title="Sandbox Details"
        description="Basic information and resource allocation"
        size="md"
      >
        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              Basic Information
            </h4>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Sandbox ID</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200 flex items-center gap-2">
                  <span className="font-mono">{sandbox.id}</span>
                  <button
                    onClick={() => handleCopy(sandbox.id, 'id')}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-200 dark:hover:text-gray-100"
                    aria-label="Copy Sandbox ID"
                  >
                    {copiedField === 'id' ? (
                      <Check className="h-4 w-4 text-success-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Labels</dt>
                <dd className="mt-1">
                  {sandbox.labels && Object.keys(sandbox.labels).length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(sandbox.labels as Record<string, string>).map(
                        ([key, value]) => (
                          <span
                            key={key}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200"
                          >
                            {key}: {value}
                          </span>
                        )
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                  )}
                </dd>
              </div>
              {/* Source: Pod containers or single disk image */}
              {isPod ? (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                    Containers ({podContainers.length})
                  </dt>
                  <dd className="space-y-3">
                    {podContainers.map((container, idx) => {
                      const borderColors = [
                        'border-l-blue-500',
                        'border-l-purple-500',
                        'border-l-teal-500',
                      ];
                      return (
                        <div
                          key={container.name}
                          className={`border border-gray-200 dark:border-[#2a2a2a] rounded-lg border-l-4 ${borderColors[idx] ?? 'border-l-gray-500'}`}
                        >
                          <div className="px-3 py-2 border-b border-gray-100 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#111] rounded-t-lg">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {container.name}
                            </span>
                          </div>
                          <dl className="px-3 py-2 space-y-2">
                            <div className="flex items-start gap-2">
                              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0 pt-0.5">
                                Disk Image
                              </dt>
                              <dd>
                                {container.diskImage?.id ? (
                                  <Link
                                    to={
                                      sandboxGroupId
                                        ? `/sandbox-groups/${encodeURIComponent(sandboxGroupId)}/disk-images/${encodeURIComponent(container.diskImage.id)}`
                                        : `/disk-images/${encodeURIComponent(container.diskImage.id)}`
                                    }
                                    className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200 font-mono break-all"
                                  >
                                    {container.diskImage.id}
                                  </Link>
                                ) : container.diskImage?.name ? (
                                  <span className="text-xs text-gray-700 dark:text-gray-300 font-mono">
                                    {container.diskImage.name}
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-500">-</span>
                                )}
                              </dd>
                            </div>
                            {container.command && container.command.length > 0 && (
                              <div className="flex items-start gap-2">
                                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0 pt-0.5">
                                  Command
                                </dt>
                                <dd>
                                  <code className="text-xs font-mono text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                                    {container.command.join(' ')}
                                  </code>
                                </dd>
                              </div>
                            )}
                            {container.args && container.args.length > 0 && (
                              <div className="flex items-start gap-2">
                                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0 pt-0.5">
                                  Args
                                </dt>
                                <dd>
                                  <code className="text-xs font-mono text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                                    {container.args.join(' ')}
                                  </code>
                                </dd>
                              </div>
                            )}
                            {container.env && Object.keys(container.env).length > 0 && (
                              <div className="flex items-start gap-2">
                                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0 pt-0.5">
                                  Env
                                </dt>
                                <dd className="flex flex-wrap gap-1">
                                  {Object.entries(container.env).map(([k, v]) => (
                                    <span
                                      key={k}
                                      className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-gray-700 dark:text-gray-300"
                                    >
                                      {k}={v}
                                    </span>
                                  ))}
                                </dd>
                              </div>
                            )}
                            {container.volumeMounts && container.volumeMounts.length > 0 && (
                              <div className="flex items-start gap-2">
                                <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0 pt-0.5">
                                  Volumes
                                </dt>
                                <dd className="flex flex-wrap gap-1">
                                  {container.volumeMounts.map((v) => (
                                    <span
                                      key={v.name}
                                      className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-gray-700 dark:text-gray-300"
                                    >
                                      {v.name} → {v.mountPath}
                                      {v.readOnly ? ' (ro)' : ''}
                                    </span>
                                  ))}
                                </dd>
                              </div>
                            )}
                            {container.resources &&
                              (container.resources.cpu || container.resources.memory) && (
                                <div className="flex items-start gap-2">
                                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0 pt-0.5">
                                    Limits
                                  </dt>
                                  <dd className="flex gap-2">
                                    {container.resources.cpu && (
                                      <Badge variant="info">{container.resources.cpu} CPU</Badge>
                                    )}
                                    {container.resources.memory && (
                                      <Badge variant="info">{container.resources.memory} Mem</Badge>
                                    )}
                                  </dd>
                                </div>
                              )}
                          </dl>
                        </div>
                      );
                    })}
                  </dd>
                </div>
              ) : (
                <>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Source Disk Image
                    </dt>
                    <dd className="mt-1">
                      {sandbox.sourcesRef?.diskImage?.id ? (
                        <Link
                          to={
                            sandboxGroupId
                              ? `/sandbox-groups/${encodeURIComponent(sandboxGroupId)}/disk-images/${encodeURIComponent(sandbox.sourcesRef.diskImage.id)}`
                              : `/disk-images/${encodeURIComponent(sandbox.sourcesRef.diskImage.id)}`
                          }
                          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200"
                        >
                          {sandbox.sourcesRef.diskImage.id}
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Entrypoint
                    </dt>
                    <dd className="mt-1">
                      {sandbox.entrypoint && sandbox.entrypoint.length > 0 ? (
                        <code className="text-sm font-mono text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                          {sandbox.entrypoint.join(' ')}
                        </code>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Cmd</dt>
                    <dd className="mt-1">
                      {sandbox.cmd && sandbox.cmd.length > 0 ? (
                        <code className="text-sm font-mono text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                          {sandbox.cmd.join(' ')}
                        </code>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                      )}
                    </dd>
                  </div>
                </>
              )}
              {/* Project + Token Expiry (only shown for project-linked sandboxes) */}
              {linkedProject && (
                <>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Project
                    </dt>
                    <dd className="mt-1 flex items-center gap-2">
                      <Link
                        to="/projects"
                        className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200"
                      >
                        {linkedProject.name}
                      </Link>
                      <a
                        href={linkedProject.repoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title={linkedProject.repoFullName}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Git Token
                    </dt>
                    <dd className="mt-1">
                      {(() => {
                        const status = linkedProject.tokenStatus;
                        const expiresAt = linkedProject.tokenExpiresAt;
                        const dot =
                          status === 'expired'
                            ? 'bg-red-500'
                            : status === 'expiring-soon'
                              ? 'bg-yellow-400'
                              : 'bg-green-500';
                        const labelCls =
                          status === 'expired'
                            ? 'text-red-600 dark:text-red-400'
                            : status === 'expiring-soon'
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-green-600 dark:text-green-400';
                        const label =
                          status === 'expired'
                            ? 'Expired'
                            : status === 'expiring-soon'
                              ? 'Expiring soon'
                              : 'Valid';
                        return (
                          <div className="space-y-0.5">
                            <span className="inline-flex items-center gap-1.5 text-sm">
                              <span className={`w-2 h-2 rounded-full ${dot} flex-shrink-0`} />
                              <span className={`${labelCls} font-medium`}>{label}</span>
                            </span>
                            {expiresAt && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 ml-3.5">
                                {status === 'expired' ? 'Expired' : 'Expires'}{' '}
                                {new Date(expiresAt).toLocaleDateString(undefined, {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </p>
                            )}
                            {!expiresAt && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 ml-3.5">
                                No expiry set
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>

          <hr className="border-gray-200 dark:border-gray-700" />

          {/* Resources */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Resources</h4>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">CPU</dt>
                <dd className="mt-1">
                  <Badge variant="info">{sandbox.resources?.cpu || '-'}</Badge>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Memory</dt>
                <dd className="mt-1">
                  <Badge variant="info">{sandbox.resources?.memory || '-'}</Badge>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Disk</dt>
                <dd className="mt-1">
                  <Badge variant="neutral">{sandbox.resources?.disk || '-'}</Badge>
                </dd>
              </div>
              {sandbox.resources?.gpu && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">GPU</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                    {sandbox.resources.gpu.sku} x{sandbox.resources.gpu.quantity}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          <hr className="border-gray-200 dark:border-gray-700" />

          {/* Exposed Ports */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Exposed Ports</h4>
              {isRunning && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setShowAddPortModal(true)}
                >
                  Add Port
                </Button>
              )}
            </div>
            {!isRunning ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Port exposure is only available when the sandbox is running.
              </p>
            ) : sandbox.ports && sandbox.ports.length > 0 ? (
              <div className="space-y-3">
                {sandbox.ports.map((port) => (
                  <div
                    key={port.name || port.port}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="info">{port.port}</Badge>
                        {port.name && (
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {port.name}
                          </span>
                        )}
                        {port.protocol === 'Http2' && <Badge variant="neutral">HTTP/2</Badge>}
                        {port.auth?.anonymous && <Badge variant="warning">Anonymous</Badge>}
                        {port.auth?.github?.enabled && !port.auth?.anonymous && (
                          <Badge variant="success">GitHub Auth</Badge>
                        )}
                        {port.auth?.entraId?.enabled && !port.auth?.anonymous && (
                          <Badge variant="success">Entra ID Auth</Badge>
                        )}
                        {!port.auth?.anonymous &&
                          !port.auth?.github?.enabled &&
                          !port.auth?.entraId?.enabled && (
                            <Badge variant="warning">Inaccessible</Badge>
                          )}
                        {port.ipAccessControl && (
                          <Badge variant="neutral">
                            IP: {port.ipAccessControl.defaultAction}
                            {port.ipAccessControl.rules.length > 0 &&
                              `, ${port.ipAccessControl.rules.length} rule(s)`}
                          </Badge>
                        )}
                        {port.cors && (
                          <Badge variant="neutral">
                            CORS: {port.cors.allowOrigins.length || 0} origin(s)
                          </Badge>
                        )}
                      </div>
                      <a
                        href={port.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mt-1"
                      >
                        <span className="font-mono text-xs break-all">
                          {maskUrlQueryString(port.url)}
                        </span>
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      </a>
                      {port.auth?.github?.enabled && (
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {(() => {
                            const parts: string[] = [];
                            if (port.auth.github.emails?.length)
                              parts.push(`${port.auth.github.emails.length} email(s)`);
                            if (port.auth.github.emailSuffixes?.length)
                              parts.push(
                                `${port.auth.github.emailSuffixes.length} email suffix(es)`
                              );
                            if (port.auth.github.usernames?.length)
                              parts.push(`${port.auth.github.usernames.length} username(s)`);
                            if (port.auth.github.usernameSuffixes?.length)
                              parts.push(
                                `${port.auth.github.usernameSuffixes.length} username suffix(es)`
                              );
                            return parts.length > 0
                              ? `Allowed: ${parts.join(', ')}`
                              : 'Any GitHub user';
                          })()}
                        </div>
                      )}
                      {port.auth?.entraId?.enabled && (
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {(() => {
                            const parts: string[] = [];
                            if (port.auth.entraId.emails?.length)
                              parts.push(`${port.auth.entraId.emails.length} email(s)`);
                            if (port.auth.entraId.emailSuffixes?.length)
                              parts.push(
                                `${port.auth.entraId.emailSuffixes.length} email suffix(es)`
                              );
                            if (port.auth.entraId.objectIds?.length)
                              parts.push(`${port.auth.entraId.objectIds.length} object ID(s)`);
                            if (port.auth.entraId.tenantIds?.length)
                              parts.push(`${port.auth.entraId.tenantIds.length} tenant ID(s)`);
                            return parts.length > 0
                              ? `Allowed: ${parts.join(', ')}`
                              : 'Any Entra ID user';
                          })()}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <button
                        onClick={() => handleCopy(port.url, `port-${port.port}`)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Copy URL"
                      >
                        {copiedField === `port-${port.port}` ? (
                          <Check className="h-4 w-4 text-success-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEditPort(port)}
                        className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                        title="Edit port"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setPortToRemove(port)}
                        className="p-1 text-gray-400 hover:text-danger-600 dark:hover:text-danger-400"
                        title="Remove port"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  No ports exposed yet. Expose a port to access services running in your sandbox.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setShowAddPortModal(true)}
                >
                  Add Port
                </Button>
              </div>
            )}
          </div>

          <hr className="border-gray-200 dark:border-gray-700" />

          {/* Connections - Preview Only */}
          {isPreviewMode && (
            <>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Connections
                  </h4>
                  {isRunning && (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Plus className="h-4 w-4" />}
                      onClick={() => setShowAddConnectionModal(true)}
                    >
                      Add Connection
                    </Button>
                  )}
                </div>
                {!isRunning ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Connections can only be added when the sandbox is running.
                  </p>
                ) : sandbox.connections && sandbox.connections.length > 0 ? (
                  <div className="space-y-2">
                    {sandbox.connections.map((connectionId) => {
                      const connectionName = getConnectionName(connectionId);
                      return (
                        <div
                          key={connectionId}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <Link2 className="h-4 w-4 text-primary-500" />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {connectionName || connectionId}
                            </span>
                            {connectionName && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                {connectionId}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No connections added yet.
                  </p>
                )}
              </div>

              <hr className="border-gray-200 dark:border-gray-700" />
            </>
          )}

          {/* Network Egress Policy */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Network Egress Policy
              </h4>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowEgressPolicyModal(true)}
                disabled={!isRunning}
                title={!isRunning ? 'Sandbox must be running to update egress policy' : undefined}
              >
                Edit
              </Button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Default:
                </span>
                <Badge variant={egressPolicyDisplayDefault === 'Allow' ? 'success' : 'danger'}>
                  {egressPolicyDisplayDefault}
                </Badge>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Inspection:
                </span>
                <Badge
                  variant={
                    (effectiveEgressPolicy?.trafficInspection ?? 'Legacy') === 'Full'
                      ? 'info'
                      : (effectiveEgressPolicy?.trafficInspection ?? 'Legacy') === 'None'
                        ? 'warning'
                        : 'neutral'
                  }
                >
                  {effectiveEgressPolicy?.trafficInspection ?? 'Legacy'}
                </Badge>
              </div>

              {egressPolicyDisplayRules.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Host rules
                  </div>
                  {egressPolicyDisplayRules.map((rule, idx) => {
                    const action = rule.action ?? egressPolicyDisplayDefault;
                    return (
                      <div
                        key={`${rule.pattern}-${idx}`}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-mono text-gray-900 dark:text-gray-100">
                            {rule.pattern}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Action: {action}
                            {rule.action ? '' : ' (default)'}
                          </span>
                        </div>
                        <Badge variant={action === 'Allow' ? 'success' : 'danger'}>{action}</Badge>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No host-specific rules configured.
                </p>
              )}

              {egressPolicyDisplayAdvancedRules.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Advanced rules
                  </div>
                  {egressPolicyDisplayAdvancedRules.map((rule, idx) => (
                    <div
                      key={`rule-${idx}`}
                      className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-mono text-gray-900 dark:text-gray-100">
                          {rule.match.host}
                          {rule.match.path ? rule.match.path : ''}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            rule.action.type === 'Transform'
                              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                              : rule.action.type === 'Rewrite'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                : rule.action.type === 'Allow'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          }`}
                        >
                          {rule.action.type}
                        </span>
                      </div>
                      {rule.name && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {rule.name}
                        </span>
                      )}
                      {rule.action.headers && rule.action.headers.length > 0 && (
                        <div className="mt-1 space-y-1">
                          {rule.action.headers.map((h, hIdx) => (
                            <span
                              key={hIdx}
                              className="block text-xs font-mono text-gray-600 dark:text-gray-400"
                            >
                              {h.operation}: {h.name}
                              {h.value ? ` = ${h.value}` : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {!isRunning && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Editing is only available when the sandbox is running.
                </p>
              )}
            </div>
          </div>

          <hr className="border-gray-200 dark:border-gray-700" />

          {/* Lifecycle Policy */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Lifecycle Policy
              </h4>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowLifecyclePolicyModal(true)}
              >
                Edit
              </Button>
            </div>
            <div className="space-y-4">
              {/* Auto-Suspend */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Auto-Suspend
                  </span>
                  <Badge
                    variant={sandbox.lifecycle?.autoSuspendPolicy?.enabled ? 'success' : 'neutral'}
                  >
                    {sandbox.lifecycle?.autoSuspendPolicy?.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                {sandbox.lifecycle?.autoSuspendPolicy?.enabled && (
                  <div className="ml-8 space-y-1">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Idle timeout:{' '}
                      {formatDurationLong(sandbox.lifecycle.autoSuspendPolicy.interval ?? 300)}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Mode: {sandbox.lifecycle.autoSuspendPolicy.mode ?? 'Memory'}
                    </p>
                  </div>
                )}
              </div>

              {/* Auto-Delete */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Trash2 className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Auto-Delete
                  </span>
                  <Badge
                    variant={sandbox.lifecycle?.autoDeletePolicy?.enabled ? 'success' : 'neutral'}
                  >
                    {sandbox.lifecycle?.autoDeletePolicy?.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                {sandbox.lifecycle?.autoDeletePolicy?.enabled && (
                  <div className="ml-8 space-y-1">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Delete after:{' '}
                      {formatDurationLong(
                        sandbox.lifecycle.autoDeletePolicy.deleteIntervalInSeconds ?? 86400
                      )}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Trigger:{' '}
                      {sandbox.lifecycle.autoDeletePolicy.trigger === 'AfterCreation'
                        ? 'After creation'
                        : 'After suspend'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </SlidePanel>
    </div>
  );
}

/* ==========================================================================
   Helper components for Network Audit content
   Extracted to avoid duplicating the audit UI across layouts.
   ========================================================================== */

interface NetworkAuditProps {
  isLoading: boolean;
  error: unknown;
  egressDecisions:
    | {
        networkEgress: { denied: EgressDecisionEntry[]; allowed: EgressDecisionEntry[] };
        statefulTcp?: { connections: StatefulTcpEgressEntry[] };
      }
    | undefined;
  egressDecisionsUpdatedAt: number;
  copiedNetworkAudit: boolean;
  isManualEgressRefresh: boolean;
  onCopy: () => void;
  onRefresh: () => void;
  getConnectionName: (id: string) => string | undefined;
}

/**
 * Network audit panel with CardHeader and CardBody — classic style.
 * Shows separate Denied / Allowed sections with colored backgrounds.
 * Used in Classic layout where the content lives inside a Card.
 */
function NetworkAuditContent({
  isLoading,
  error,
  egressDecisions,
  egressDecisionsUpdatedAt,
  copiedNetworkAudit,
  isManualEgressRefresh,
  onCopy,
  onRefresh,
}: NetworkAuditProps) {
  const unified = buildUnifiedEgress(egressDecisions);
  return (
    <>
      <CardHeader
        title="Network Audit"
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={
                copiedNetworkAudit ? (
                  <Check className="h-4 w-4 text-success-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )
              }
              onClick={onCopy}
              disabled={isLoading || !!error}
            >
              {copiedNetworkAudit ? 'Copied' : 'Copy'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={
                <RefreshCw className={`h-4 w-4 ${isManualEgressRefresh ? 'animate-spin' : ''}`} />
              }
              onClick={onRefresh}
              disabled={isManualEgressRefresh}
            >
              {isManualEgressRefresh ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        }
      />
      <CardBody className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading audit log...</p>
        ) : error ? (
          <p className="text-sm text-danger-600 dark:text-danger-400">Failed to load audit log.</p>
        ) : (
          <div className="flex-1 flex flex-col min-h-0 space-y-3">
            {egressDecisionsUpdatedAt > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                Last updated: {new Date(egressDecisionsUpdatedAt).toLocaleString()}
              </p>
            )}

            {/* Denied egress — HTTP requests + TCP connections denied by policy */}
            <div
              className={`flex flex-col min-h-0 ${unified.denied.length > 0 ? 'flex-1' : 'flex-shrink-0'}`}
            >
              <div className="flex items-center gap-2 flex-shrink-0 mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Denied</span>
                <Badge variant="danger">{unified.denied.length}</Badge>
              </div>
              {unified.denied.length > 0 ? (
                <div
                  className="flex-1 overflow-y-auto min-h-0 space-y-1"
                  tabIndex={0}
                  role="region"
                  aria-label="Denied egress list"
                >
                  {unified.denied.map((entry) => (
                    <EgressEntryRow key={entry.id} entry={entry} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No denied egress.</p>
              )}
            </div>

            {/* Allowed egress — HTTP requests + TCP connections permitted by policy */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex items-center gap-2 flex-shrink-0 mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Allowed
                </span>
                <Badge variant="success">{unified.allowed.length}</Badge>
              </div>
              {unified.allowed.length > 0 ? (
                <div
                  className="flex-1 overflow-y-auto min-h-0 space-y-1"
                  tabIndex={0}
                  role="region"
                  aria-label="Allowed egress list"
                >
                  {unified.allowed.map((entry) => (
                    <EgressEntryRow key={entry.id} entry={entry} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No allowed egress recorded.
                </p>
              )}
            </div>
          </div>
        )}
      </CardBody>
    </>
  );
}

/**
 * Inner network audit content without Card wrapper.
 * Renders a flat list of audit entries sorted by timestamp (newest first).
 * Used in the IDE layout tabbed panel where the outer container is provided by the tab.
 */
function NetworkAuditInnerContent({
  isLoading,
  error,
  egressDecisions,
  egressDecisionsUpdatedAt,
}: NetworkAuditProps) {
  const unified = useMemo(() => buildUnifiedEgress(egressDecisions), [egressDecisions]);
  const mergedEntries = useMemo(
    () => [...unified.denied, ...unified.allowed].sort((a, b) => b.timestampMs - a.timestampMs),
    [unified]
  );

  if (isLoading) {
    return (
      <div className="p-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading audit log...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <p className="text-sm text-danger-600 dark:text-danger-400">Failed to load audit log.</p>
      </div>
    );
  }

  const deniedCount = unified.denied.length;
  const allowedCount = unified.allowed.length;

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Summary bar */}
      <div className="flex items-center gap-3 px-3.5 py-2 text-[11px] text-gray-500 dark:text-gray-400 flex-shrink-0 border-b border-gray-200 dark:border-[#2a2a2a]/50">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
          <span>{allowedCount} allowed</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
          <span>{deniedCount} denied</span>
        </div>
        {egressDecisionsUpdatedAt > 0 && (
          <span className="ml-auto">
            Updated {new Date(egressDecisionsUpdatedAt).toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Audit list */}
      <div
        className="flex-1 overflow-y-auto py-1"
        tabIndex={0}
        role="region"
        aria-label="Network audit log entries"
      >
        {mergedEntries.length === 0 ? (
          <div className="p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">No egress activity recorded.</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {mergedEntries.map((entry) => (
              <EgressEntryRow key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
